import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, Download, Pin, FileText, AlertCircle, Lightbulb, Copy, Check, ExternalLink, ZoomIn, ZoomOut, RotateCcw, RotateCw, Image as ImageIcon, MessageSquare, MessageSquarePlus, History, Loader2, ChevronUp, ChevronDown, Trash2, Sparkles, Minimize2, Maximize2, Type, Edit3 } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from './ui/input';
import { Dialog, DialogContent } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { documentApi, DocumentContent, chatApi, taskApi, userApi, DocumentComment } from '@/services/api';
import Markdown from 'markdown-to-jsx';
import { CustomQuestionSection } from './CustomQuestionSection';
import { CustomPromptModal } from './CustomPromptModal';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useCompactMode } from '@/hooks/useCompactMode';
import { useDocumentFontSize, DOC_FONT_SIZE_OPTIONS } from '@/hooks/useDocumentFontSize';
import { standardDocumentPrompts } from '@/config/standardDocumentPrompts';
import { TaskResultAttachment } from '@/stores/sessionStore';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';

interface DocumentViewerProps {
  document: {
    resource_id?: string;
    file_name?: string;
    file_type?: string;
    type?: string; // from active documents
    title?: string; // from active documents
  } | null;
  onClose: () => void;
  isFullWidth?: boolean;
  onViewApplication?: (applicationNumber: string) => void;
}

export function DocumentViewer({ document, onClose, isFullWidth = false, onViewApplication }: DocumentViewerProps) {
  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [documentContent, setDocumentContent] = useState<DocumentContent | null>(null);
  const [copiedParagraph, setCopiedParagraph] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Highlighting state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [claimsFilterText, setClaimsFilterText] = useState('');
  const [debouncedClaimsFilterText, setDebouncedClaimsFilterText] = useState('');
  const [claimsFilterEnabled, setClaimsFilterEnabled] = useState(false);
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [filterSearchResults, setFilterSearchResults] = useState(false);
  const [claimsFilterMatchCount, setClaimsFilterMatchCount] = useState(0);
  const [currentClaimsFilterIndex, setCurrentClaimsFilterIndex] = useState(0);
  const [referenceNumeral, setReferenceNumeral] = useState('');
  const [debouncedReferenceNumeral, setDebouncedReferenceNumeral] = useState('');
  const [referenceNumeralMatchCount, setReferenceNumeralMatchCount] = useState(0);
  const [currentReferenceNumeralIndex, setCurrentReferenceNumeralIndex] = useState(0);
  const [filterReferenceNumerals, setFilterReferenceNumerals] = useState(false);

  // Pinned highlights state - array of {term, type: 'search' | 'reference', color, cssColor}
  // Using both Tailwind classes (for UI badges) and CSS hex values (for inline styles in content)
  const PINNED_COLORS = [
    { class: 'bg-pink-300', css: '#f9a8d4' },
    { class: 'bg-purple-300', css: '#d8b4fe' },
    { class: 'bg-blue-300', css: '#93c5fd' },
    { class: 'bg-cyan-300', css: '#67e8f9' },
    { class: 'bg-orange-300', css: '#fdba74' },
    { class: 'bg-rose-300', css: '#fda4af' },
    { class: 'bg-indigo-300', css: '#a5b4fc' },
    { class: 'bg-teal-300', css: '#5eead4' },
  ];
  const [pinnedHighlights, setPinnedHighlights] = useState<Array<{term: string, type: 'search' | 'reference', color: string, cssColor: string}>>([]);

  // Comments state
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionParagraphIndex, setSelectionParagraphIndex] = useState<number | null>(null);
  const [selectionSection, setSelectionSection] = useState('');
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [commentPopupPosition, setCommentPopupPosition] = useState({ x: 0, y: 0 });
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const pinCurrentHighlight = (type: 'search' | 'reference') => {
    const term = type === 'search' ? debouncedSearchQuery : debouncedReferenceNumeral;
    if (!term.trim()) return;

    // Check if already pinned
    if (pinnedHighlights.some(p => p.term.toLowerCase() === term.toLowerCase() && p.type === type)) return;

    // Get next available color
    const usedColors = pinnedHighlights.map(p => p.color);
    const nextColor = PINNED_COLORS.find(c => !usedColors.includes(c.class)) || PINNED_COLORS[pinnedHighlights.length % PINNED_COLORS.length];

    setPinnedHighlights([...pinnedHighlights, { term, type, color: nextColor.class, cssColor: nextColor.css }]);
  };

  const unpinHighlight = (term: string, type: 'search' | 'reference') => {
    setPinnedHighlights(pinnedHighlights.filter(p => !(p.term.toLowerCase() === term.toLowerCase() && p.type === type)));
  };

  const clearAllPinnedHighlights = () => {
    setPinnedHighlights([]);
  };

  // Helper function to apply unified highlighting (search, reference numerals, pinned)
  const applyUnifiedHighlighting = (content: string, options?: { includeRefNum?: boolean }) => {
    const query = debouncedSearchQuery.trim();
    const refNum = options?.includeRefNum ? debouncedReferenceNumeral.trim() : '';
    const hasPinnedHighlights = pinnedHighlights.length > 0;

    if (!query && !refNum && !hasPinnedHighlights) {
      return content;
    }

    const matches: Array<{ start: number; end: number; type: string; text: string; cssColor?: string }> = [];
    const lowerContent = content.toLowerCase();

    // Find all query matches
    if (query) {
      const lowerQuery = query.toLowerCase();
      let pos = 0;
      while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
        matches.push({
          start: pos,
          end: pos + query.length,
          type: 'search',
          text: content.substring(pos, pos + query.length)
        });
        pos++;
      }
    }

    // Find all reference numeral matches
    if (refNum) {
      const lowerRefNum = refNum.toLowerCase();
      let pos = 0;
      while ((pos = lowerContent.indexOf(lowerRefNum, pos)) !== -1) {
        matches.push({
          start: pos,
          end: pos + refNum.length,
          type: 'refnum',
          text: content.substring(pos, pos + refNum.length)
        });
        pos++;
      }
    }

    // Find all pinned highlight matches
    if (hasPinnedHighlights) {
      pinnedHighlights.forEach(pinned => {
        const lowerTerm = pinned.term.toLowerCase();
        let pos = 0;
        while ((pos = lowerContent.indexOf(lowerTerm, pos)) !== -1) {
          matches.push({
            start: pos,
            end: pos + pinned.term.length,
            type: 'pinned',
            text: content.substring(pos, pos + pinned.term.length),
            cssColor: pinned.cssColor
          });
          pos++;
        }
      });
    }

    if (matches.length === 0) {
      return content;
    }

    // Sort by length (longer first) to ensure longer matches take priority
    matches.sort((a, b) => {
      const lenDiff = (b.end - b.start) - (a.end - a.start);
      if (lenDiff !== 0) return lenDiff;
      return a.start - b.start;
    });

    // Build non-overlapping ranges
    const usedRanges: typeof matches = [];
    matches.forEach(match => {
      const overlaps = usedRanges.some(range =>
        (match.start >= range.start && match.start < range.end) ||
        (match.end > range.start && match.end <= range.end) ||
        (match.start <= range.start && match.end >= range.end)
      );
      if (!overlaps) {
        usedRanges.push(match);
      }
    });

    // Sort by position for building result
    usedRanges.sort((a, b) => a.start - b.start);

    // Build highlighted text
    let result = '';
    let lastEnd = 0;

    usedRanges.forEach(match => {
      if (match.start > lastEnd) {
        result += content.substring(lastEnd, match.start);
      }

      if (match.type === 'search') {
        result += `<mark style="background-color: #fde047; color: black;">${match.text}</mark>`;
      } else if (match.type === 'refnum') {
        result += `<mark style="background-color: #93c5fd; color: black;">${match.text}</mark>`;
      } else if (match.type === 'pinned') {
        result += `<mark style="background-color: ${match.cssColor}; color: black;">${match.text}</mark>`;
      }

      lastEnd = match.end;
    });

    if (lastEnd < content.length) {
      result += content.substring(lastEnd);
    }

    return result;
  };

  // Check if any highlighting is active
  const hasAnyHighlighting = debouncedSearchQuery.trim() || debouncedReferenceNumeral.trim() || pinnedHighlights.length > 0;

  // Pinning state
  const [claimsPinned, setClaimsPinned] = useState(false);
  const [drawingsPinned, setDrawingsPinned] = useState(false);

  // Drawings state
  const [selectedDrawingIndex, setSelectedDrawingIndex] = useState<number | null>(null);
  const [drawingZoom, setDrawingZoom] = useState(100);
  const [drawingRotation, setDrawingRotation] = useState(0);
  const [drawingViewerWidth, setDrawingViewerWidth] = useState(320);
  const [isResizingDrawingWidth, setIsResizingDrawingWidth] = useState(false);
  const [drawingsUrls, setDrawingsUrls] = useState<string[]>([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  // Question History state
  const [showQuestionHistory, setShowQuestionHistory] = useState(false);
  const [showQuestionHistoryFullscreen, setShowQuestionHistoryFullscreen] = useState(false);
  const [questions, setQuestions] = useState<Array<{
    input_text: string;
    response: string | { output: string };
    timestamp: string;
  }>>([]);
  const [questionText, setQuestionText] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionWidth, setQuestionWidth] = useState(370);
  const [isResizingQuestion, setIsResizingQuestion] = useState(false);
  const [showTopButton, setShowTopButton] = useState(false);
  const [showBottomButton, setShowBottomButton] = useState(false);
  const [customPrompts, setCustomPrompts] = useState<Array<{ prompt_id: string; label: string; prompt: string; available_in?: string }>>([]);
  const [showCustomPromptModal, setShowCustomPromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<{ id: string; label: string; prompt: string; available_in?: string } | null>(null);
  const [showQuickPromptsMenu, setShowQuickPromptsMenu] = useState(false);
  const { compactMode, setCompactMode } = useCompactMode();
  const { documentFontSize, setDocumentFontSize, getFontSizeStyle } = useDocumentFontSize();

  // Filter custom prompts for document context (document or both)
  const filteredCustomPrompts = useMemo(() => {
    return customPrompts.filter(p =>
      !p.available_in || p.available_in === 'document' || p.available_in === 'both'
    );
  }, [customPrompts]);

  // Task History state
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [taskHistory, setTaskHistory] = useState<Array<{
    analysis_id: string;
    task_type: string;
    task_name?: string;
    result?: any;
    analysis?: any;
    timestamp?: string;
    time_received?: string;
    doc_id?: string;
    selected_number?: string;
    application_number?: string;
  }>>([]);
  const [taskHistoryLoading, setTaskHistoryLoading] = useState(false);
  const [taskHistoryWidth, setTaskHistoryWidth] = useState(370);
  const [isResizingTaskHistory, setIsResizingTaskHistory] = useState(false);
  const [selectedTaskResult, setSelectedTaskResult] = useState<TaskResultAttachment | null>(null);
  const [showTaskResultViewer, setShowTaskResultViewer] = useState(false);

  // Refs
  const claimsSidebarRef = useRef<HTMLDivElement>(null);
  const drawingViewerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const questionScrollRef = useRef<HTMLDivElement>(null);
  const taskHistoryScrollRef = useRef<HTMLDivElement>(null);

  // Get sections from document content
  const getSections = useCallback(() => {
    const description = documentContent?.description;
    console.log('[getSections] description type:', typeof description, 'value:', description);

    if (!description) {
      console.log('[getSections] No description found');
      return [];
    }

    if (typeof description === 'string') {
      console.log('[getSections] Description is a string, returning empty (will use processedDescription)');
      return [];
    }

    if (typeof description === 'object' && !Array.isArray(description)) {
      // Type guard to check if it has sections property
      const hasSection = 'sections' in description;
      console.log('[getSections] Description is object, has sections?:', hasSection);

      if (hasSection) {
        const sectionsData = Array.isArray((description as { sections?: any[] }).sections)
          ? (description as { sections?: any[] }).sections
          : null;

        if (sectionsData) {
          console.log('[getSections] Found sections array with', sectionsData.length, 'sections');
          const sections = sectionsData.map(section => {
            // Find the first paragraph that has a number property
            const firstNumberedPara = section.paragraphs?.find((para: any) => para.number !== undefined);
            const minParaNum = firstNumberedPara ? parseInt(String(firstNumberedPara.number)) : Infinity;

            console.log(`Section "${section.title}": minParagraphNumber = ${minParaNum}`);

            return {
              title: section.title,
              paragraphs: section.paragraphs?.map((para: any) => ({
                number: para.number ? String(para.number).padStart(4, '0') : '',
                displayNumber: para.number ? String(para.number).padStart(4, '0') : '',
                content: para.text || para.content,
                originalNumber: para.number ? parseInt(String(para.number)) : 0,
                heading: para.heading
              })) || [],
              minParagraphNumber: minParaNum
            };
          });

          return sections.sort((a, b) => a.minParagraphNumber - b.minParagraphNumber);
        }
      }

      // Fallback: Try to parse as flat dictionary (paragraph_number: text)
      const entries = Object.entries(description as Record<string, any>);
      console.log('[getSections] Trying flat dictionary format with', entries.length, 'entries');

      if (entries.length > 0) {
        // Check if keys look like paragraph numbers (either pure numeric or "para_NNNN" format)
        const firstKey = entries[0][0];
        const isNumericKey = /^\d+$/.test(firstKey);
        const isParaKey = /^para_\d+$/.test(firstKey);
        console.log('[getSections] First key:', firstKey, 'is numeric?:', isNumericKey, 'is para_NNNN?:', isParaKey);

        if (isNumericKey) {
          return [{
            title: 'Description',
            paragraphs: entries
              .map(([number, content]) => ({
                number: String(number).padStart(4, '0'),
                displayNumber: String(number).padStart(4, '0'),
                content: typeof content === 'string' ? content : content?.text || content?.content || String(content)
              }))
              .sort((a, b) => parseInt(a.number) - parseInt(b.number)),
            minParagraphNumber: parseInt(entries[0][0]) || 0
          }];
        } else if (isParaKey) {
          // Handle "para_NNNN" format
          console.log('[getSections] Detected para_NNNN format, extracting paragraph numbers');
          return [{
            title: 'Description',
            paragraphs: entries
              .map(([key, content]) => {
                // Extract number from "para_0012" -> "0012" -> 12
                const match = key.match(/^para_(\d+)$/);
                const paraNum = match ? parseInt(match[1]) : 0;
                return {
                  number: String(paraNum).padStart(4, '0'),
                  displayNumber: String(paraNum).padStart(4, '0'),
                  content: typeof content === 'string' ? content : content?.text || content?.content || String(content),
                  originalNumber: paraNum
                };
              })
              .sort((a, b) => a.originalNumber - b.originalNumber),
            minParagraphNumber: 0
          }];
        }
      }
    }

    console.log('[getSections] No valid format found, returning empty');
    return [];
  }, [documentContent?.description]);

  // Handle text selection for adding comments
  const handleTextSelection = useCallback((e: MouseEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setShowCommentPopup(false);
      return;
    }

    const text = selection.toString().trim();
    if (text.length < 3) {
      setShowCommentPopup(false);
      return;
    }

    // Find the paragraph index and offset from the selection
    const anchorNode = selection.anchorNode;
    let paragraphIndex = -1;
    let section = 'content';
    let textOffset = 0;

    if (anchorNode) {
      let parent = anchorNode.parentElement;
      const selectionText = selection.toString();

      console.log('[Selection] Selected text:', selectionText.substring(0, 50));
      console.log('[Selection] Current active tab:', activeTab);

      // Determine which tab we're in by checking parent elements
      let tabSection = activeTab; // Default to active tab name

      // Map tab names to section names
      const tabToSection: { [key: string]: string } = {
        'overview': 'overview',
        'specification': 'specification',
        'claims': 'claims',
        'abstract': 'abstract',
        'content': 'content'
      };

      section = tabToSection[activeTab] || activeTab;

      // Get sections data to match against (for specification tab)
      const sections = getSections();
      console.log('[Selection] getSections() returned', sections.length, 'sections');

      if (sections.length > 0) {
        console.log('[Selection] Section titles:', sections.map(s => s.title));
        console.log('[Selection] Total paragraphs:', sections.reduce((sum, s) => sum + s.paragraphs.length, 0));
      }

      // Try to find which section and which paragraph contains the selected text
      let matchedSection: any = null;
      let matchedParagraph: any = null;
      let paragraphTextOffset = -1;

      // If we have sections (specification tab), find the actual selected instance using context
      if (sections.length > 0 && activeTab === 'specification') {
        console.log('[Selection] Finding actual selection using surrounding context...');

        // Get surrounding context from the DOM to make the search unique
        const range = selection.getRangeAt(0);
        const container = range.startContainer;

        // Get the full text content from the containing element
        let fullContextText = '';
        let selectionRelativeOffset = -1;

        // Find the prose container
        let proseElement = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement;
        while (proseElement && !proseElement.classList.contains('prose') && proseElement !== window.document.body) {
          proseElement = proseElement.parentElement;
        }

        if (proseElement) {
          fullContextText = proseElement.textContent || '';

          // Find where our selection appears in this full context
          const beforeSelection = fullContextText.substring(0, fullContextText.indexOf(selectionText));
          selectionRelativeOffset = beforeSelection.length;

          console.log('[Selection] Full context length:', fullContextText.length);
          console.log('[Selection] Selection starts at offset:', selectionRelativeOffset);

          // Get a unique context snippet (100 chars before + selection + 50 chars after)
          const contextStart = Math.max(0, selectionRelativeOffset - 100);
          const contextEnd = Math.min(fullContextText.length, selectionRelativeOffset + selectionText.length + 50);
          const contextSnippet = fullContextText.substring(contextStart, contextEnd);

          console.log('[Selection] Context snippet:', contextSnippet.substring(0, 100) + '...');

          // Now search for this context in the paragraphs
          for (const sec of sections) {
            for (const para of sec.paragraphs) {
              if (!para.content) continue;

              // Check if this paragraph contains the context snippet
              const paraText = para.content;
              if (paraText.includes(contextSnippet)) {
                // Found the paragraph! Now find the exact offset of the selection within it
                const snippetOffsetInPara = paraText.indexOf(contextSnippet);
                const selectionOffsetInSnippet = contextSnippet.indexOf(selectionText);
                paragraphTextOffset = snippetOffsetInPara + selectionOffsetInSnippet;

                matchedSection = sec;
                matchedParagraph = para;

                console.log(`[Selection] ✓ Found using context in section "${sec.title}"`);
                console.log(`[Selection] ✓ Paragraph originalNumber: ${para.originalNumber}`);
                console.log(`[Selection] ✓ Offset within paragraph: ${paragraphTextOffset}`);
                break;
              }

              // Also try with normalized whitespace (remove markdown artifacts, etc.)
              const normalizedPara = paraText.replace(/\s+/g, ' ').trim();
              const normalizedSnippet = contextSnippet.replace(/\s+/g, ' ').trim();

              if (normalizedPara.includes(normalizedSnippet)) {
                const snippetOffsetInPara = normalizedPara.indexOf(normalizedSnippet);
                const selectionOffsetInSnippet = normalizedSnippet.indexOf(selectionText.replace(/\s+/g, ' ').trim());
                paragraphTextOffset = snippetOffsetInPara + selectionOffsetInSnippet;

                matchedSection = sec;
                matchedParagraph = para;

                console.log(`[Selection] ✓ Found using normalized context in section "${sec.title}"`);
                console.log(`[Selection] ✓ Paragraph originalNumber: ${para.originalNumber}`);
                console.log(`[Selection] ✓ Offset within paragraph: ${paragraphTextOffset}`);
                break;
              }
            }
            if (matchedParagraph) break;
          }
        }

        if (!matchedParagraph) {
          console.log('[Selection] ⚠ Could not find using context, falling back to simple search...');

          // Fallback: use first occurrence
          for (const sec of sections) {
            for (const para of sec.paragraphs) {
              if (!para.content) continue;
              const offset = para.content.indexOf(selectionText);
              if (offset !== -1) {
                matchedSection = sec;
                matchedParagraph = para;
                paragraphTextOffset = offset;
                console.log(`[Selection] Using first occurrence in paragraph ${para.originalNumber}`);
                break;
              }
            }
            if (matchedParagraph) break;
          }
        }
      } else {
        console.log('[Selection] Skipping section search - activeTab:', activeTab, ', sections.length:', sections.length);
      }

      if (matchedSection && matchedParagraph) {
        // Use the paragraph's actual original number
        if (matchedParagraph.originalNumber > 0) {
          paragraphIndex = matchedParagraph.originalNumber;
        } else if (matchedSection.minParagraphNumber !== Infinity) {
          // Fallback: use section's min paragraph number
          paragraphIndex = matchedSection.minParagraphNumber;
        } else {
          paragraphIndex = 0;
        }

        textOffset = paragraphTextOffset;
        section = matchedSection.title || section;

        console.log('[Selection] Using paragraph originalNumber:', matchedParagraph.originalNumber);
        console.log('[Selection] Calculated actual paragraph index:', paragraphIndex);
      } else {
        // For other tabs (overview, claims, abstract), search in the active tab's content
        const allProseElements = Array.from(window.document.querySelectorAll('.prose, pre.whitespace-pre-wrap')) as HTMLElement[];
        console.log('[Selection] Searching', allProseElements.length, 'content areas for:', selectionText.substring(0, 50));

        for (const element of allProseElements) {
          const elementText = element.textContent || '';
          const offset = elementText.indexOf(selectionText);

          if (offset !== -1) {
            textOffset = offset;
            paragraphIndex = 0; // For non-specification tabs, use 0 or simple count
            console.log('[Selection] Found in', section, 'tab at offset:', offset);
            break;
          }
        }
      }

      console.log('[Selection] Final result:', { paragraphIndex, textOffset, section, text: text.substring(0, 50) });
    }

    setSelectedText(text);
    setSelectionParagraphIndex(paragraphIndex);
    setSelectionSection(section);
    // Store the offset for precise matching later
    (window as any).__lastCommentOffset = textOffset;
    setCommentPopupPosition({ x: e.clientX, y: e.clientY });
    setShowCommentPopup(true);
  }, [activeTab, getSections]); // Need activeTab to detect current tab

  useEffect(() => {
    if (document) {
      loadDocument();
      // Reset to overview tab when loading a new document
      setActiveTab('overview');
    }
  }, [document]);

  // Listen for text selection to show comment popup
  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Only process if clicking within the content area
      if (!contentRef.current?.contains(e.target as Node)) {
        return;
      }
      handleTextSelection(e);
    };

    window.document.addEventListener('mouseup', handleMouseUp);
    return () => window.document.removeEventListener('mouseup', handleMouseUp);
  }, [handleTextSelection]);

  // Watch for sessionApplications changes to update application number
  useEffect(() => {
    if (!document || !documentContent || !sessionApplications) return;

    const docType = document.file_type || document.type?.toLowerCase() || 'patent';
    if (docType !== 'patent' && docType !== 'publication') return;
    if (documentContent.application_number) return; // Already has application number

    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      docId = numberMatch[0];
    }

    // Remove country code (US) and kind code (A1, B2, etc.) - keep only numbers
    // First remove US prefix, then remove kind code pattern (letter + 1-2 digits at end), then keep only numbers
    const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
    console.log('Watching for app (effect). Type:', docType, 'Original docId:', docId, '→ Clean:', cleanDocId, 'Total apps:', sessionApplications.length);

    const matchingApp = sessionApplications.find((app: any) => {
      const metaData = app.application?.applicationMetaData;
      if (!metaData) {
        console.log('  Skipping app (no metaData)');
        return false;
      }

      if (docType === 'patent' && metaData.patentNumber) {
        const patentNum = metaData.patentNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
        console.log('  (effect) Checking patent:', metaData.patentNumber, '→', patentNum, 'vs', cleanDocId, 'match?', patentNum === cleanDocId);
        return patentNum === cleanDocId;
      } else if (docType === 'publication' && metaData.earliestPublicationNumber) {
        const pubNum = metaData.earliestPublicationNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
        console.log('  (effect) Checking publication:', metaData.earliestPublicationNumber, '→', pubNum, 'vs', cleanDocId, 'match?', pubNum === cleanDocId);
        return pubNum === cleanDocId;
      } else {
        console.log('  Skipping app (no matching field for', docType, ')');
      }
      return false;
    });

    if (matchingApp) {
      const appNumber = matchingApp.application_id || matchingApp.application?.applicationNumberText;
      if (appNumber) {
        console.log('✓ Found matching application (effect):', appNumber, 'for document:', docId);
        setDocumentContent(prev => prev ? {
          ...prev,
          application_number: appNumber
        } : prev);
      }
    } else {
      console.log('✗ No matching application found (effect) for:', docId);
    }
  }, [sessionApplications, document, documentContent]);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce claims filter text (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClaimsFilterText(claimsFilterText);
    }, 300);
    return () => clearTimeout(timer);
  }, [claimsFilterText]);

  // Debounce reference numeral (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferenceNumeral(referenceNumeral);
    }, 300);
    return () => clearTimeout(timer);
  }, [referenceNumeral]);

  // Reset search match count when search query changes
  useEffect(() => {
    setSearchMatchCount(0);
    setCurrentSearchIndex(0);
  }, [debouncedSearchQuery]);

  // Reset claims filter match count when claims filter changes
  useEffect(() => {
    setClaimsFilterMatchCount(0);
    setCurrentClaimsFilterIndex(0);
  }, [debouncedClaimsFilterText]);

  // Reset reference numeral match count when reference numeral changes
  useEffect(() => {
    setReferenceNumeralMatchCount(0);
    setCurrentReferenceNumeralIndex(0);
  }, [debouncedReferenceNumeral]);

  // Recalculate match counts when active tab changes
  useEffect(() => {
    // Wait a bit for the tab content to render
    const timer = setTimeout(() => {
      // Get only the currently active/visible tab content panel
      const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);

      if (!activeTabContent) {
        // Fallback: just query the whole document if we can't find the tab panel
        if (debouncedSearchQuery) {
          const matches = window.document.querySelectorAll('.search-match');
          setSearchMatchCount(matches.length);
          setCurrentSearchIndex(0);
        }
        if (debouncedReferenceNumeral) {
          const refMatches = window.document.querySelectorAll('.reference-numeral-match');
          setReferenceNumeralMatchCount(refMatches.length);
          setCurrentReferenceNumeralIndex(0);
        }
        if (claimsFilterEnabled && debouncedClaimsFilterText) {
          const claimsMatches = window.document.querySelectorAll('.claims-filter-match');
          setClaimsFilterMatchCount(claimsMatches.length);
        }
        return;
      }

      // Count search matches in the currently visible tab only
      if (debouncedSearchQuery) {
        const matches = activeTabContent.querySelectorAll('.search-match');
        setSearchMatchCount(matches.length);
        setCurrentSearchIndex(0);

        // Scroll to first match
        if (matches.length > 0) {
          matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          matches.forEach((m, i) => {
            if (i === 0) {
              m.classList.add('ring-2', 'ring-blue-500');
            } else {
              m.classList.remove('ring-2', 'ring-blue-500');
            }
          });
        }
      }

      // Count reference numeral matches in the currently visible tab only
      if (debouncedReferenceNumeral) {
        const refMatches = activeTabContent.querySelectorAll('.reference-numeral-match');
        setReferenceNumeralMatchCount(refMatches.length);
        setCurrentReferenceNumeralIndex(0);

        // Scroll to first match
        if (refMatches.length > 0) {
          refMatches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          refMatches.forEach((m, i) => {
            if (i === 0) {
              m.classList.add('ring-2', 'ring-blue-500');
            } else {
              m.classList.remove('ring-2', 'ring-blue-500');
            }
          });
        }
      }

      // Count claims filter matches if applicable
      if (claimsFilterEnabled && debouncedClaimsFilterText) {
        const claimsMatches = activeTabContent.querySelectorAll('.claims-filter-match');
        setClaimsFilterMatchCount(claimsMatches.length);
        setCurrentClaimsFilterIndex(0);
      }
    }, 150); // Delay to ensure tab content is rendered

    return () => clearTimeout(timer);
  }, [activeTab, debouncedSearchQuery, debouncedReferenceNumeral, debouncedClaimsFilterText, claimsFilterEnabled]);

  // Count all matches after DOM updates (only in active tab)
  useEffect(() => {
    if (!documentContent?.description) {
      return;
    }

    // Wait for DOM to update with highlighted content
    const timer = setTimeout(() => {
      // Get only the currently active/visible tab content panel
      const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);

      // Fallback to querying whole document if tab panel not found
      const searchContext = activeTabContent || window.document;

      if (debouncedSearchQuery.trim()) {
        const searchMatches = searchContext.querySelectorAll('.search-match');
        setSearchMatchCount(searchMatches.length);
      }

      if (debouncedClaimsFilterText.trim()) {
        const claimsMatches = searchContext.querySelectorAll('.claims-filter-match');
        setClaimsFilterMatchCount(claimsMatches.length);
      }

      if (debouncedReferenceNumeral.trim()) {
        const refMatches = searchContext.querySelectorAll('.reference-numeral-match');
        setReferenceNumeralMatchCount(refMatches.length);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [debouncedSearchQuery, debouncedClaimsFilterText, debouncedReferenceNumeral, documentContent, activeTab]);

  // Load drawings when sidebar is opened for patents/publications
  useEffect(() => {
    console.log('Drawings useEffect triggered! drawingsPinned:', drawingsPinned, 'document:', document);
    const loadDrawings = async () => {
      if (!drawingsPinned) {
        // Reset drawings state when sidebar is closed
        setDrawingsUrls([]);
        setSelectedDrawingIndex(null);
        setDrawingZoom(100);
        setDrawingRotation(0);
        return;
      }

      if (!document) {
        console.log('No document object found');
        return;
      }

      const docType = document.file_type || document.type || 'Document';
      console.log('Document type for drawings:', docType, 'Full document:', document);

      if (!['patent', 'publication'].includes(docType.toLowerCase())) {
        console.log('Document type not patent or publication, skipping drawings');
        return;
      }

      // Get document ID
      let docId = document.resource_id || document.title || '';
      docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
      docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');
      const numberMatch = docId.match(/\d+/);
      if (numberMatch && docType !== 'upload') {
        docId = numberMatch[0];
      }

      console.log('Loading drawings for:', { docType, docId, original: document.resource_id || document.title, fullDocument: document });

      setLoadingDrawings(true);
      try {
        const response = await documentApi.fetchDrawingsInfo(docType, docId);
        if (response && response.urls) {
          setDrawingsUrls(response.urls);
          if (response.urls.length > 0) {
            setSelectedDrawingIndex(0);
          }
        }
      } catch (error) {
        console.error('Error loading drawings:', error);
      } finally {
        setLoadingDrawings(false);
      }
    };

    loadDrawings();
  }, [drawingsPinned, document]);

  // Listen for mouseup in claims sidebar to capture completed selections
  useEffect(() => {
    const handleMouseUp = () => {
      setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();

        if (selectedText && selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;

          // Check if selection is inside claims sidebar
          if (claimsSidebarRef.current && claimsSidebarRef.current.contains(container as Node)) {
            setClaimsFilterText(selectedText);
            setClaimsFilterEnabled(true);

            // Clear selection after a brief moment
            setTimeout(() => {
              if (window.getSelection) {
                window.getSelection()?.removeAllRanges();
              }
            }, 50);
          }
        }
      }, 10);
    };

    window.document.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Handle drawing viewer width resize
  useEffect(() => {
    if (!isResizingDrawingWidth) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!drawingViewerRef.current) return;

      const containerRect = drawingViewerRef.current.getBoundingClientRect();
      const newWidth = Math.max(200, Math.min(800, containerRect.right - e.clientX));
      setDrawingViewerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingDrawingWidth(false);
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingDrawingWidth]);

  // Handle question panel width resize
  useEffect(() => {
    if (!isResizingQuestion) return;

    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const minDocContentWidth = 570;
      const maxQuestionWidth = windowWidth - minDocContentWidth;
      const newWidth = Math.max(300, Math.min(maxQuestionWidth, windowWidth - mouseX));
      setQuestionWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingQuestion(false);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.cursor = 'col-resize';
    window.document.body.style.userSelect = 'none';

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
  }, [isResizingQuestion]);

  // Handle task history panel width resize
  useEffect(() => {
    if (!isResizingTaskHistory) return;

    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const minDocContentWidth = 570;
      const maxTaskHistoryWidth = windowWidth - minDocContentWidth;
      const newWidth = Math.max(300, Math.min(maxTaskHistoryWidth, windowWidth - mouseX));
      setTaskHistoryWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingTaskHistory(false);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.cursor = 'col-resize';
    window.document.body.style.userSelect = 'none';

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
  }, [isResizingTaskHistory]);

  // Scroll management for question panel
  const scrollToTop = useCallback(() => {
    if (questionScrollRef.current) {
      questionScrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollHeight } = questionScrollRef.current;
      questionScrollRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const checkScrollNeed = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollHeight, clientHeight } = questionScrollRef.current;
      if (scrollHeight >= 2 * clientHeight) {
        setShowTopButton(false);
        setShowBottomButton(true);
      } else {
        setShowTopButton(false);
        setShowBottomButton(false);
      }
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = questionScrollRef.current;
      setShowBottomButton(!(scrollTop + clientHeight >= scrollHeight - 10));
      setShowTopButton(!(scrollTop === 0));
    }
  }, []);

  // Toggle fullscreen for question history
  const toggleQuestionHistoryFullscreen = useCallback(() => {
    setShowQuestionHistoryFullscreen(!showQuestionHistoryFullscreen);
    // Scroll to bottom when opening fullscreen
    if (!showQuestionHistoryFullscreen) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [showQuestionHistoryFullscreen, scrollToBottom]);

  // Load question history when panel is opened
  const handleOpenQuestionHistory = useCallback(async () => {
    if (!document) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    console.log('[QuestionHistory] Loading history for:', { docId, docType, documentRaw: document });

    try {
      const history = await documentApi.getCustomQuestionHistory(docId, docType);
      console.log('[QuestionHistory] Loaded history:', history);
      setQuestions(history);
      setShowQuestionHistory(true);
    } catch (error) {
      console.error('[QuestionHistory] Failed to load question history:', error);
      setShowQuestionHistory(true); // Still show panel even if loading fails
    }
  }, [document]);

  // Reload question history when document changes while panel is open
  useEffect(() => {
    if (showQuestionHistory && document) {
      const docType = (document.file_type || document.type || '').toLowerCase();
      let docId = document.resource_id || document.title || '';
      docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
      docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

      console.log('[QuestionHistory] Document changed, reloading history for:', { docId, docType });

      documentApi.getCustomQuestionHistory(docId, docType)
        .then(history => {
          console.log('[QuestionHistory] Reloaded history:', history);
          setQuestions(history);
        })
        .catch(error => {
          console.error('[QuestionHistory] Failed to reload question history:', error);
          setQuestions([]); // Clear questions on error
        });
    }
  }, [document, showQuestionHistory]);

  // Submit a custom question
  const handleQuestionSubmit = useCallback(async () => {
    if (!document || !questionText.trim() || questionLoading) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    console.log('[QuestionHistory] Submitting question:', { question: questionText, docId, docType });

    setQuestionLoading(true);

    // Add optimistic UI update
    const newQuestion = {
      input_text: questionText,
      response: '...',
      timestamp: new Date().toISOString(),
    };
    setQuestions([...questions, newQuestion]);
    const currentQuestion = questionText;
    setQuestionText('');
    scrollToBottom();

    try {
      // Submit question
      const response = await documentApi.customQuestion(currentQuestion, docType, docId);
      console.log('[QuestionHistory] Question submitted, response:', response);

      if (response.error) {
        throw new Error(response.error);
      }

      const messageId = response.message_id;
      if (!messageId) {
        throw new Error('No message ID returned');
      }

      // Poll for response every 7.5 seconds
      const pollInterval = setInterval(async () => {
        try {
          console.log('[QuestionHistory] Polling for message:', messageId);
          const messageResponse = await chatApi.getMessage(messageId);
          console.log('[QuestionHistory] Poll response:', messageResponse);

          if (messageResponse.message &&
              messageResponse.message !== 'null' &&
              messageResponse.message !== null &&
              messageResponse.message !== undefined) {

            console.log('[QuestionHistory] Got response, refreshing history');
            // Refresh question history from backend
            const history = await documentApi.getCustomQuestionHistory(docId, docType);
            console.log('[QuestionHistory] History after response:', history);
            setQuestions(history);
            setQuestionLoading(false);
            scrollToBottom();
            clearInterval(pollInterval);
          }
        } catch (pollError) {
          console.error('[QuestionHistory] Polling error:', pollError);
        }
      }, 7500);

      // Clear interval after 5 minutes to prevent infinite polling
      setTimeout(() => {
        clearInterval(pollInterval);
        if (questionLoading) {
          setQuestionLoading(false);
          // Update the last question with error message
          setQuestions(prev => {
            const updated = [...prev];
            if (updated.length > 0) {
              updated[updated.length - 1].response = 'Error: Response timeout';
            }
            return updated;
          });
        }
      }, 300000); // 5 minutes

    } catch (error) {
      console.error('Failed to submit question:', error);
      setQuestionLoading(false);
      // Update the last question with error message
      setQuestions(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].response = 'Error generating response';
        }
        return updated;
      });
    }
  }, [document, questionText, questionLoading, questions, scrollToBottom]);

  // Fetch custom prompts on mount
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await userApi.fetchPrompts();
        if (response.prompts) {
          setCustomPrompts(response.prompts);
        }
      } catch (error) {
        console.error('Failed to fetch custom prompts:', error);
      }
    };
    fetchPrompts();
  }, []);

  // Handle deleting a question
  const handleDeleteQuestion = useCallback(async (queryId: string, index: number) => {
    if (!document) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    try {
      // Optimistically remove from UI
      setQuestions(prev => prev.filter((_, i) => i !== index));

      // Call backend to delete (if you have this API endpoint)
      // await documentApi.deleteCustomQuestion(queryId, docType, docId);
    } catch (error) {
      console.error('Failed to delete question:', error);
      // Reload questions on error
      const history = await documentApi.getCustomQuestionHistory(docId, docType);
      setQuestions(history);
    }
  }, [document]);

  // Handle creating/editing custom prompt
  const handleCreatePrompt = useCallback(() => {
    setEditingPrompt(null);
    setShowCustomPromptModal(true);
  }, []);

  // Handle saving custom prompt
  const handleSaveCustomPrompt = useCallback(async (label: string, prompt: string, availableIn: string) => {
    try {
      if (editingPrompt && editingPrompt.id) {
        // Update existing prompt
        await userApi.updatePrompt(editingPrompt.id, label, prompt, availableIn);
      } else {
        // Create new prompt
        await userApi.savePrompt(label, prompt, availableIn);
      }

      // Refresh prompts
      const response = await userApi.fetchPrompts();
      if (response.prompts) {
        setCustomPrompts(response.prompts);
      }

      setShowCustomPromptModal(false);
      setEditingPrompt(null);
    } catch (error) {
      console.error('Failed to save custom prompt:', error);
      throw error; // Let modal handle the error
    }
  }, [editingPrompt]);

  // Auto-scroll to bottom in fullscreen modal when questions change
  useEffect(() => {
    if (showQuestionHistoryFullscreen && questions.length > 0 && !questionLoading) {
      scrollToBottom();
    }
  }, [showQuestionHistoryFullscreen, questions.length, questionLoading, scrollToBottom]);

  // Helper functions for task result parsing and rendering
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;
    return resultObj;
  };

  const getParsedResult = (output: any) => {
    if (!output) return null;
    if (typeof output === 'object') return output;

    if (typeof output === 'string') {
      const trimmed = output.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return output;
      }

      try {
        return JSON.parse(output);
      } catch {
        if (output.includes("'")) {
          try {
            let jsonStr = output;
            jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');
            jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');
            jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');
            jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');
            jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');
            jsonStr = jsonStr.replace(/True/g, 'true');
            jsonStr = jsonStr.replace(/False/g, 'false');
            jsonStr = jsonStr.replace(/None/g, 'null');
            return JSON.parse(jsonStr);
          } catch (e) {
            console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
            return output;
          }
        }
        console.warn('Failed to parse JSON:', output.substring(0, 100) + '...');
        return output;
      }
    }

    return output;
  };

  const renderTaskResultDisplay = (taskResult: TaskResultAttachment) => {
    const parsedData = getParsedResult(taskResult.content);
    const commonProps = { result: parsedData, theme: 'light' as const, showHeader: true };
    const taskType = taskResult.taskType;

    switch (taskType) {
      case 'parts_list':
        return <PartsListDisplay {...commonProps} showToggle={true} />;
      case 'spec_map':
        return <SpecMapDisplay {...commonProps} showToggle={true} />;
      case 'claims_support':
        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
      case 'claims_with_numerals':
        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
      case 'custom_analysis':
        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
      case 'ir_metadata':
        return <IRMetadataDisplay {...commonProps} />;
      case 'ir_innovation':
        return <IRInnovationAnalysisDisplay {...commonProps} />;
      case 'ir_tech_classification':
        return <IRTechClassificationDisplay {...commonProps} />;
      case 'draft_summary':
        return <DraftSummaryDisplay {...commonProps} />;
      case 'draft_detailed_description':
        return <DraftDetailedDescriptionDisplay {...commonProps} />;
      case 'draft_background':
        return <DraftBackgroundDisplay {...commonProps} />;
      case 'draft_claims':
        return <DraftClaimsDisplay {...commonProps} />;
      case 'draft_other_embodiments':
        return <OtherEmbodimentsDisplay {...commonProps} />;
      case 'oa_extract':
        return <OAExtractDisplay {...commonProps} />;
      case 'oa_analyze':
        return <OAAnalyzeDisplay {...commonProps} />;
      case 'oa_summary_art':
        return <OASummaryArtDisplay {...commonProps} />;
      case 'oa_timeline':
        return <OATimelineDisplay {...commonProps} />;
      case 'translate_document':
        return <TranslateDocumentDisplay {...commonProps} />;
      default:
        return (
          <div className="p-4 rounded-lg bg-gray-50">
            <pre className="text-xs overflow-auto whitespace-pre-wrap">
              {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  const loadDocument = async () => {
    if (!document) return;

    setLoading(true);
    try {
      // Check if document already has content (e.g., MPEP, CFR, USC)
      if ((document as any).content) {
        // For documents with pre-loaded content, create a structured content object
        setDocumentContent({
          title: (document as any).title || document.file_name || 'Document',
          abstract: '',
          description: '',
          claims: '',
          summary: null,
          content: (document as any).content,
          contentType: (document as any).contentType || 'html' // Mark content type for rendering
        } as any);
        setLoading(false);
        return;
      }

      // Determine document type
      let docType = document.file_type || document.type?.toLowerCase() || 'patent';

      // Convert "Foreign Patent" display name to API format
      if (docType === 'foreign patent') {
        docType = 'foreign_patent';
      }

      // Get raw document ID
      let docId = document.resource_id || document.title || '';

      // Clean up document ID to extract just the number
      // Remove type prefixes like "Patent ", "Publication ", etc.
      docId = docId.replace(/^(Patent|Publication|Application|Foreign Patent|Upload)\s+/i, '').trim();

      // Remove any compound prefixes like "publication-publication-" or "patent-patent-"
      docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');

      // For active documents that have format like "Patent 12345", just extract the number
      // BUT skip this for foreign patents (they need full format like EP2245553A1)
      const numberMatch = docId.match(/\d+/);
      if (numberMatch && docType !== 'upload' && docType !== 'foreign_patent') {
        docId = numberMatch[0];
      }

      console.log('Loading document:', { docType, docId, original: document.resource_id || document.title });

      // Fetch document content from API
      const content = await documentApi.fetchDocument(docType, docId);

      // Check if document was found (status: "not_found" means it needs onboarding)
      if (content && (content as any).status !== 'not_found') {
        setDocumentContent(content);

        // For patents and publications, find the application number from sessionApplications
        if ((docType === 'patent' || docType === 'publication') && sessionApplications) {
          try {
            // Clean the docId to match against patent/publication numbers
            // Remove country code (US) and kind code (A1, B2, etc.) - keep only numbers
            // First remove US prefix, then remove kind code pattern (letter + 1-2 digits at end), then keep only numbers
            const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
            console.log('Searching for application number. Type:', docType, 'Original docId:', docId, '→ Clean:', cleanDocId, 'Total apps:', sessionApplications.length);

            // Search through sessionApplications for a matching patent or publication
            const matchingApp = sessionApplications.find((app: any) => {
              const metaData = app.application?.applicationMetaData;
              if (!metaData) return false;

              if (docType === 'patent' && metaData.patentNumber) {
                // For patents: remove US prefix, kind code, and any non-numeric characters
                const patentNum = metaData.patentNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                console.log('  Checking patent:', metaData.patentNumber, '→', patentNum, 'vs', cleanDocId, 'match?', patentNum === cleanDocId);
                return patentNum === cleanDocId;
              } else if (docType === 'publication' && metaData.earliestPublicationNumber) {
                // For publications: remove US prefix, kind code (A1, B2, etc.), and any non-numeric characters
                const pubNum = metaData.earliestPublicationNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                console.log('  Checking publication:', metaData.earliestPublicationNumber, '→', pubNum, 'vs', cleanDocId, 'match?', pubNum === cleanDocId);
                return pubNum === cleanDocId;
              }
              return false;
            });

            if (matchingApp) {
              const appNumber = matchingApp.application_id || matchingApp.application?.applicationNumberText;
              if (appNumber) {
                console.log('✓ Found matching application:', appNumber, 'for document:', docId);
                setDocumentContent(prev => prev ? {
                  ...prev,
                  application_number: appNumber
                } : prev);
              }
            } else {
              console.log('✗ No matching application found for:', docId);
            }
          } catch (error) {
            console.error('Failed to find application number:', error);
            // Don't fail the whole load if application number lookup fails
          }
        }
      } else {
        // Document not found/not onboarded - try to onboard it for patents and publications
        if (docType === 'patent' || docType === 'publication') {
          console.log('Document not onboarded, triggering onboarding for:', docType, docId);
          setIsOnboarding(true);

          const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);
          addNotification({
            type: 'info',
            title: `Onboarding ${resourceTypeName}`,
            message: `${docId} is not in your session. Loading it now...`,
            duration: 10000,
          });

          try {
            const onboardResponse = await documentApi.fetchAndLoadDocument(docType, docId);

            if (onboardResponse?.job_id) {
              // Poll for job completion
              const pollForCompletion = async () => {
                const maxAttempts = 60; // 2 minutes max
                let attempts = 0;

                while (attempts < maxAttempts) {
                  await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
                  attempts++;

                  try {
                    // Try to fetch the document again
                    const retryContent = await documentApi.fetchDocument(docType, docId);
                    // Check if document is now available (not "not_found")
                    if (retryContent && (retryContent as any).status !== 'not_found') {
                      setDocumentContent(retryContent);
                      setIsOnboarding(false);
                      addNotification({
                        type: 'success',
                        title: `${resourceTypeName} Ready`,
                        message: `${docId} has been loaded successfully.`,
                        duration: 5000,
                      });
                      return;
                    }
                  } catch (pollError) {
                    console.log('Polling attempt', attempts, '- document not ready yet');
                  }
                }

                // Timeout
                setIsOnboarding(false);
                addNotification({
                  type: 'warning',
                  title: 'Still Loading',
                  message: `${docId} is taking longer than expected. It may appear in your documents shortly.`,
                  duration: 8000,
                });
              };

              pollForCompletion();
            } else {
              setIsOnboarding(false);
              addNotification({
                type: 'error',
                title: 'Failed to Load',
                message: `Could not start loading ${docId}. Please try again.`,
                duration: 5000,
              });
            }
          } catch (onboardError) {
            console.error('Failed to onboard document:', onboardError);
            setIsOnboarding(false);
            addNotification({
              type: 'error',
              title: 'Onboarding Failed',
              message: `Failed to load ${docId}. Please try again.`,
              duration: 5000,
            });
          }
        } else {
          console.error('Failed to load document content');
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse summary if it's JSON
  const parseSummary = (summary: string | object | undefined): string | object | null => {
    if (!summary) return null;

    if (typeof summary === 'string') {
      try {
        let maybeJson = summary;
        const trimmed = summary.trim();

        // Try to fix simple single-quoted JSON (Python dict format)
        if (trimmed.startsWith("{") && trimmed.includes("'")) {
          // Replace single quotes with double quotes
          maybeJson = trimmed
            .replace(/([,{])\s*'([^']+?)'\s*:/g, '$1"$2":')
            .replace(/:\s*'([^']+?)'\s*([},])/g, ':"$1"$2');
        }

        const parsed = JSON.parse(maybeJson);
        return parsed;
      } catch {
        // If not JSON, return as plain string
        return summary;
      }
    }

    if (typeof summary === 'object') {
      return summary;
    }

    return String(summary);
  };

  // Get claims array
  const getClaims = () => {
    if (!documentContent?.claims) return [];

    // Handle string format (HTML) - don't parse, return empty for now
    // Claims will be shown in their own tab if they exist as string
    if (typeof documentContent.claims === 'string') {
      return [];
    }

    let claimsArray: Array<{ claim_number: string; text: string; dependencies?: string[] }> = [];

    // Handle array format (patents)
    if (Array.isArray(documentContent.claims)) {
      claimsArray = documentContent.claims.map(claim => ({
        ...claim,
        claim_number: String(claim.claim_number || '').replace(/^0+/, '') // Remove leading zeros
      }));
    }
    // Handle dictionary format (foreign patents)
    else if (typeof documentContent.claims === 'object') {
      claimsArray = Object.entries(documentContent.claims as Record<string, any>)
        .map(([key, value]) => ({
          claim_number: String(value.claim_number || key).replace(/^0+/, ''),
          text: value.claim_text || value.text || String(value),
          dependencies: value.dependencies || []
        }));
    }

    // Sort claims by claim_number in ascending numerical order
    return claimsArray.sort((a, b) => {
      const numA = parseInt(a.claim_number) || 0;
      const numB = parseInt(b.claim_number) || 0;
      return numA - numB;
    });
  };

  const documentName = document?.file_name || document?.title || document?.resource_id || 'Document';
  const documentType = document?.file_type || document?.type || 'Document';

  // Get clean doc ID for comments
  const getDocId = () => {
    let docId = document?.resource_id || document?.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Foreign Patent|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch && documentType.toLowerCase() !== 'upload' && documentType.toLowerCase() !== 'foreign_patent') {
      docId = numberMatch[0];
    }
    return docId;
  };

  // Load comments when document changes
  useEffect(() => {
    const loadComments = async () => {
      const docId = getDocId();
      const docType = documentType.toLowerCase();
      console.log('[DocumentViewer] Loading comments for:', { docType, docId, resourceId: document?.resource_id });
      if (docId && docType) {
        const loadedComments = await documentApi.getDocumentComments(docType, docId);
        console.log('[DocumentViewer] Loaded comments:', loadedComments);
        setComments(loadedComments);
      }
    };
    loadComments();
  }, [document?.resource_id, documentType]);

  // Highlight commented text in the document
  useEffect(() => {
    console.log('[Highlighting] Effect triggered. Comments:', comments.length);
    if (comments.length === 0) {
      console.log('[Highlighting] Skipping - no comments');
      return;
    }

    const highlightCommentedText = () => {
      // Search in ALL document areas (all tabs) - not just the currently visible one
      const allProseElements = Array.from(window.document.querySelectorAll('.prose, pre.whitespace-pre-wrap')) as HTMLElement[];

      console.log('[Highlighting] Found', allProseElements.length, 'potential content areas');
      console.log('[Highlighting] Starting highlighting for', comments.length, 'comments');

      // Remove existing comment highlights from all areas
      allProseElements.forEach(element => {
        const existingHighlights = element.querySelectorAll('.comment-highlight');
        existingHighlights.forEach(el => {
          const parent = el.parentNode;
          if (parent) {
            parent.replaceChild(window.document.createTextNode(el.textContent || ''), el);
            parent.normalize();
          }
        });
      });

      // Add highlights for each comment - search in specific section only
      comments.forEach((comment, idx) => {
        const text = comment.selected_text;
        console.log('[Highlighting] Processing comment', idx, ':', text.substring(0, 50), 'section:', comment.section, 'paragraph:', comment.paragraph_index);
        if (!text || text.length < 3) return;

        // Normalize the search text
        const normalizedSearch = text.replace(/\s+/g, ' ').trim().toLowerCase();

        // Variables to track where we found the text
        let foundInElement: HTMLElement | null = null;
        let globalIndex = -1;
        let normalizedAllText = '';

        // Check if this is a specification comment (has section title like "BACKGROUND OF THE INVENTION")
        const isSpecificationComment = comment.section !== 'overview' &&
                                      comment.section !== 'claims' &&
                                      comment.section !== 'abstract' &&
                                      comment.section !== 'content';

        if (isSpecificationComment) {
          // Find the specific paragraph in the section using getSections()
          const sections = getSections();
          let targetParagraph: any = null;
          let targetSection: any = null;

          // Find the section and paragraph that matches this comment
          for (const sec of sections) {
            if (sec.title === comment.section) {
              targetSection = sec;
              // Find the paragraph with matching originalNumber
              targetParagraph = sec.paragraphs.find((p: any) => p.originalNumber === comment.paragraph_index);
              if (targetParagraph) {
                console.log('[Highlighting] Found target paragraph', comment.paragraph_index, 'in section', comment.section);
                break;
              }
            }
          }

          // If we found the specific paragraph, only search within its content
          if (targetParagraph && targetParagraph.content) {
            const paragraphText = targetParagraph.content;
            const normalizedParagraph = paragraphText.replace(/\s+/g, ' ').trim().toLowerCase();
            const paragraphIndex = normalizedParagraph.indexOf(normalizedSearch);

            if (paragraphIndex === -1) {
              console.log('[Highlighting] Text not found in target paragraph');
              return;
            }

            // Now find this paragraph's text in the DOM
            // Use a unique snippet from the paragraph to identify it
            const uniqueSnippet = paragraphText.substring(0, Math.min(50, paragraphText.length));

            for (const element of allProseElements) {
              const allText = element.textContent || '';
              if (allText.includes(uniqueSnippet)) {
                foundInElement = element;
                normalizedAllText = allText.replace(/\s+/g, ' ').trim().toLowerCase();
                globalIndex = normalizedAllText.indexOf(normalizedSearch);
                console.log('[Highlighting] Found paragraph in DOM element:', element.className);
                break;
              }
            }

            if (!foundInElement) {
              console.log('[Highlighting] Paragraph not found in DOM');
              return;
            }
          }
        } else {
          // For tab-level comments (overview, claims, abstract, content)
          // Only highlight if we're on the matching tab
          console.log('[Highlighting] Tab-level comment for section:', comment.section, 'current tab:', activeTab);

          // Search in the current content - should only be visible if on the right tab
          for (const element of allProseElements) {
            // Check if element is currently visible (simple heuristic)
            if (element.offsetParent === null) continue; // Skip hidden elements

            const allText = element.textContent || '';
            normalizedAllText = allText.replace(/\s+/g, ' ').trim().toLowerCase();
            globalIndex = normalizedAllText.indexOf(normalizedSearch);

            if (globalIndex !== -1) {
              foundInElement = element;
              console.log('[Highlighting] Found in visible element:', element.className, 'at index:', globalIndex);
              break;
            }
          }
        }

        if (!foundInElement || globalIndex === -1) {
          console.log('[Highlighting] Text not found in any content area');
          return;
        }

        // Use TreeWalker to find text nodes in the element where we found the text
        const walker = window.document.createTreeWalker(
          foundInElement,
          NodeFilter.SHOW_TEXT,
          null
        );

        const nodesToReplace: { node: Node; start: number; end: number }[] = [];
        let node;
        let textNodesChecked = 0;
        let currentPosition = 0;

        while ((node = walker.nextNode())) {
          textNodesChecked++;
          const nodeText = node.textContent || '';
          const nodeNormalized = nodeText.replace(/\s+/g, ' ').toLowerCase();

          // Check if this node contains part of the search text
          for (let i = 0; i < nodeText.length; i++) {
            const remainingNormalized = normalizedAllText.substring(currentPosition).replace(/\s+/g, ' ');
            if (remainingNormalized.startsWith(normalizedSearch)) {
              // Found the start of the match
              console.log('[Highlighting] Found match starting in text node', textNodesChecked, 'at position', i);
              nodesToReplace.push({
                node,
                start: i,
                end: Math.min(i + text.length, nodeText.length)
              });
              break;
            }
            currentPosition++;
          }

          if (nodesToReplace.length > 0) break;
        }

        console.log('[Highlighting] Checked', textNodesChecked, 'text nodes, found', nodesToReplace.length, 'matches');

        // Replace text nodes with highlighted spans
        nodesToReplace.forEach(({ node, start, end }) => {
          const nodeText = node.textContent || '';
          const before = nodeText.substring(0, start);
          const match = nodeText.substring(start, end);
          const after = nodeText.substring(end);

          const span = window.document.createElement('span');
          span.className = 'comment-highlight bg-yellow-100 hover:bg-yellow-200 cursor-pointer border-b-2 border-yellow-400 transition-colors';
          span.textContent = match;
          span.dataset.commentId = comment.id;
          span.title = comment.comment_text;

          // Add click handler to open comments panel
          span.addEventListener('click', () => {
            setShowCommentsPanel(true);
          });

          const fragment = window.document.createDocumentFragment();
          if (before) fragment.appendChild(window.document.createTextNode(before));
          fragment.appendChild(span);
          if (after) fragment.appendChild(window.document.createTextNode(after));

          node.parentNode?.replaceChild(fragment, node);
          console.log('[Highlighting] Added highlight span for comment', idx);
        });
      });
    };

    // Delay to ensure DOM is fully rendered (especially after dangerouslySetInnerHTML)
    const timer = setTimeout(() => {
      highlightCommentedText();
    }, 200); // Increased delay to ensure tab content is rendered

    return () => clearTimeout(timer);
  }, [comments, documentContent, activeTab]); // Re-highlight when switching tabs

  // Add comment
  const addComment = async () => {
    if (!newCommentText.trim() || !selectedText) return;

    const textOffset = (window as any).__lastCommentOffset || 0;
    const newComment: DocumentComment = {
      id: crypto.randomUUID(),
      paragraph_index: selectionParagraphIndex ?? -1,
      selected_text: selectedText,
      comment_text: newCommentText,
      section: selectionSection,
      text_offset: textOffset,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const updatedComments = [...comments, newComment];
    setComments(updatedComments);

    // Save to backend
    const docId = getDocId();
    const docType = documentType.toLowerCase();
    console.log('[DocumentViewer] Saving comments for:', { docType, docId, resourceId: document?.resource_id, numComments: updatedComments.length });
    const success = await documentApi.saveDocumentComments(docType, docId, updatedComments);

    if (success) {
      addNotification({ type: 'success', title: 'Comment Added', message: 'Your comment has been saved.', duration: 3000 });
    } else {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to save comment.', duration: 3000 });
    }

    setNewCommentText('');
    setShowCommentPopup(false);
    setSelectedText('');
  };

  // Update comment
  const updateComment = async (commentId: string) => {
    if (!editingCommentText.trim()) return;

    const updatedComments = comments.map(c =>
      c.id === commentId
        ? { ...c, comment_text: editingCommentText, updated_at: new Date().toISOString() }
        : c
    );
    setComments(updatedComments);

    const docId = getDocId();
    const docType = documentType.toLowerCase();
    await documentApi.saveDocumentComments(docType, docId, updatedComments);

    setEditingCommentId(null);
    setEditingCommentText('');
    addNotification({ type: 'success', title: 'Comment Updated', message: 'Your comment has been updated.', duration: 3000 });
  };

  // Delete comment
  const deleteComment = async (commentId: string) => {
    const updatedComments = comments.filter(c => c.id !== commentId);
    setComments(updatedComments);

    const docId = getDocId();
    const docType = documentType.toLowerCase();
    await documentApi.saveDocumentComments(docType, docId, updatedComments);
    addNotification({ type: 'info', title: 'Comment Deleted', message: 'Your comment has been removed.', duration: 3000 });
  };

  console.log('DocumentViewer render - documentType:', documentType, 'document:', document);

  const summary = parseSummary(documentContent?.summary);
  const sections = getSections();
  const claims = getClaims();

  // Process description with highlighting
  const processedDescription = useMemo(() => {
    console.log('=== processedDescription useMemo ===');
    console.log('documentContent.description type:', typeof documentContent?.description);

    if (!documentContent?.description) {
      console.log('[processedDescription] No description');
      return '';
    }

    // Check if description is a string (HTML) or object (structured sections)
    if (typeof documentContent.description !== 'string') {
      console.log('[processedDescription] Description is not a string, returning empty');
      return ''; // Return empty string if it's structured - will use sections rendering instead
    }

    const descString = documentContent.description;
    console.log('[processedDescription] Description string length:', descString.length);

    // Check if it looks like HTML (contains HTML tags)
    const hasHTMLTags = /<[a-z][\s\S]*>/i.test(descString);
    console.log('[processedDescription] Has HTML tags?:', hasHTMLTags);

    // If it's plain text without HTML tags, wrap it in paragraph tags for display
    let htmlContent: string;
    if (!hasHTMLTags) {
      console.log('[processedDescription] Plain text detected, wrapping in paragraphs');
      // Split by double newlines for paragraphs, or single newlines if no doubles
      const paragraphs = descString.includes('\n\n')
        ? descString.split('\n\n')
        : descString.split('\n');

      htmlContent = paragraphs
        .filter(p => p.trim())
        .map(p => `<p>${p.trim()}</p>`)
        .join('\n');
    } else {
      console.log('[processedDescription] HTML detected, proceeding with reordering');
      htmlContent = descString;
    }

    // Continue to highlighting logic below (don't return early)

    // Parse and reorder sections by paragraph number
    const reorderSections = (htmlContent: string): string => {
      console.log('=== reorderSections called ===');
      console.log('HTML content length:', htmlContent.length);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Find all elements
      const allElements = Array.from(doc.body.children);

      // Create array of elements with their sort numbers
      const elementsWithNumbers: Array<{ element: Element; sortNum: number | null }> = [];

      allElements.forEach((elem, idx) => {
        const elemText = elem.textContent || '';
        // Look for paragraph numbers like [0001], [0030], etc.
        const paraMatch = elemText.match(/\[(\d{4})\]/) || elemText.match(/\[(\d{1,3})\]/);

        let sortNum: number | null = null;

        if (paraMatch) {
          sortNum = parseInt(paraMatch[1]);

          // Handle [0000] - hide the number but keep the element
          if (sortNum === 0) {
            const walker = window.document.createTreeWalker(elem, NodeFilter.SHOW_TEXT, null);
            let node;
            while ((node = walker.nextNode())) {
              const text = node.textContent || '';
              if (text.match(/\[0{4}\]/) || text.match(/\[0\]/)) {
                node.textContent = text.replace(/\[0{4}\]\s*/, '').replace(/\[0\]\s*/, '');
                break;
              }
            }
            sortNum = null; // Treat as no number for sorting
          }
        } else {
          // Element has no paragraph number - check if it's a section heading
          const isBoldHeading = elem.classList.contains('font-bold') ||
                               elem.querySelector('.font-bold') !== null ||
                               elem.tagName === 'H1' || elem.tagName === 'H2' || elem.tagName === 'H3';

          if (isBoldHeading) {
            // Look ahead to find the next element with a paragraph number
            for (let i = idx + 1; i < allElements.length; i++) {
              const nextElemText = allElements[i].textContent || '';
              const nextParaMatch = nextElemText.match(/\[(\d{4})\]/) || nextElemText.match(/\[(\d{1,3})\]/);

              if (nextParaMatch) {
                const nextParaNum = parseInt(nextParaMatch[1]);
                if (nextParaNum > 0) {
                  // Use this paragraph number minus 0.5 to ensure heading sorts just before its section
                  sortNum = nextParaNum - 0.5;
                  break;
                }
              }
            }
          }
        }

        elementsWithNumbers.push({ element: elem, sortNum });
      });

      // Separate elements with sort numbers from those without
      const withNumbers = elementsWithNumbers.filter(item => item.sortNum !== null);
      const withoutNumbers = elementsWithNumbers.filter(item => item.sortNum === null);

      // Debug logging
      console.log('Paragraph numbers found:', withNumbers.map(item => item.sortNum).sort((a, b) => a! - b!));
      console.log('Total elements with numbers:', withNumbers.length);
      console.log('Total elements without numbers:', withoutNumbers.length);

      // Sort elements that have sort numbers
      withNumbers.sort((a, b) => (a.sortNum! - b.sortNum!));

      // Rebuild HTML: First, elements with numbers (sorted), then elements without numbers
      const newBody = window.document.createElement('div');

      withNumbers.forEach(item => {
        newBody.appendChild(item.element.cloneNode(true));
      });

      withoutNumbers.forEach(item => {
        newBody.appendChild(item.element.cloneNode(true));
      });

      return newBody.innerHTML;
    };

    const highlightSearchQuery = (htmlContent: string) => {
      const query = debouncedSearchQuery.trim();
      const claimsFilter = debouncedClaimsFilterText.trim();

      // Check if we have any highlights to apply (search, claimsFilter, or pinned)
      if (!query && !claimsFilter && pinnedHighlights.length === 0) {
        return { html: htmlContent, searchCount: 0 };
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      let searchCount = 0;

      const highlightTextNodes = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const lowerText = text.toLowerCase();

          const hasQuery = query && lowerText.includes(query.toLowerCase());
          const hasClaimsFilter = claimsFilter && lowerText.includes(claimsFilter.toLowerCase());
          const hasPinnedMatch = pinnedHighlights.some(p => lowerText.includes(p.term.toLowerCase()));

          if (!hasQuery && !hasClaimsFilter && !hasPinnedMatch) {
            return;
          }

          // Unified highlighting approach - collect all matches
          const matches: Array<{ start: number; end: number; type: string; color: string; cssColor?: string; text: string }> = [];

          // Find all query matches
          if (query) {
            const lowerQuery = query.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerQuery, pos)) !== -1) {
              matches.push({ start: pos, end: pos + query.length, type: 'search', color: 'bg-yellow-300', text: text.substring(pos, pos + query.length) });
              pos++;
            }
          }

          // Find all claims filter matches
          if (claimsFilter) {
            const lowerClaimsFilter = claimsFilter.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerClaimsFilter, pos)) !== -1) {
              matches.push({ start: pos, end: pos + claimsFilter.length, type: 'claims', color: 'bg-green-300', text: text.substring(pos, pos + claimsFilter.length) });
              pos++;
            }
          }

          // Find all pinned highlight matches
          pinnedHighlights.forEach(pinned => {
            const lowerTerm = pinned.term.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerTerm, pos)) !== -1) {
              matches.push({ start: pos, end: pos + pinned.term.length, type: 'pinned', color: pinned.color, cssColor: pinned.cssColor, text: text.substring(pos, pos + pinned.term.length) });
              pos++;
            }
          });

          if (matches.length === 0) return;

          // Sort by length (longer first), then by start position
          matches.sort((a, b) => {
            const lenDiff = (b.end - b.start) - (a.end - a.start);
            if (lenDiff !== 0) return lenDiff;
            return a.start - b.start;
          });

          // Build non-overlapping ranges (search/claims take priority over pinned)
          const usedRanges: Array<{ start: number; end: number }> = [];
          const finalMatches: typeof matches = [];
          matches.forEach(match => {
            const overlaps = usedRanges.some(range =>
              (match.start >= range.start && match.start < range.end) ||
              (match.end > range.start && match.end <= range.end) ||
              (match.start <= range.start && match.end >= range.end)
            );

            if (!overlaps) {
              usedRanges.push({ start: match.start, end: match.end });
              finalMatches.push(match);
            }
          });

          finalMatches.sort((a, b) => a.start - b.start);

          // Build highlighted text
          const highlightParts: Node[] = [];
          let lastEnd = 0;

          finalMatches.forEach(match => {
            if (match.start > lastEnd) {
              highlightParts.push(window.document.createTextNode(text.substring(lastEnd, match.start)));
            }

            const mark = window.document.createElement('mark');
            if (match.type === 'search') {
              searchCount++;
              mark.className = 'search-match';
              mark.style.backgroundColor = '#fde047'; // yellow-300
              mark.style.color = 'black';
              mark.setAttribute('data-match-index', String(searchCount - 1));
            } else if (match.type === 'claims') {
              mark.className = 'claims-filter-match';
              mark.style.backgroundColor = '#86efac'; // green-300
              mark.style.color = 'black';
            } else {
              mark.className = 'pinned-highlight';
              mark.style.backgroundColor = match.cssColor || '#f9a8d4'; // Use inline style with CSS color
              mark.style.color = 'black';
            }
            mark.textContent = match.text;
            highlightParts.push(mark);
            lastEnd = match.end;
          });

          if (lastEnd < text.length) {
            highlightParts.push(window.document.createTextNode(text.substring(lastEnd)));
          }

          const span = window.document.createElement('span');
          highlightParts.forEach(part => span.appendChild(part));
          if (node.parentNode) {
            node.parentNode.replaceChild(span, node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.tagName !== 'MARK') {
            Array.from(node.childNodes).forEach(child => highlightTextNodes(child));
          }
        }
      };

      highlightTextNodes(doc.body);
      return { html: doc.body.innerHTML, searchCount };
    };

    const filterDescriptionByMatches = (htmlContent: string) => {
      const hasSearchFilter = filterSearchResults && debouncedSearchQuery.trim();
      const hasClaimsFilter = claimsFilterEnabled && debouncedClaimsFilterText.trim();

      if (!hasSearchFilter && !hasClaimsFilter) return htmlContent;

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const allElements = doc.body.querySelectorAll('p, div');

      allElements.forEach(elem => {
        let shouldShow = false;

        if (hasSearchFilter || hasClaimsFilter) {
          const searchMarks = hasSearchFilter ? elem.querySelectorAll('.search-match') : [];
          const claimsMarks = hasClaimsFilter ? elem.querySelectorAll('.claims-filter-match') : [];

          if (searchMarks.length > 0 || claimsMarks.length > 0) {
            shouldShow = true;
          }
        }

        const isHeading = elem.classList.contains('font-bold');

        if (isHeading) {
          let nextElem = elem.nextElementSibling;
          let foundInSection = false;
          while (nextElem && !nextElem.classList.contains('font-bold')) {
            const searchMarksInNext = hasSearchFilter ? nextElem.querySelectorAll('.search-match') : [];
            const claimsMarksInNext = hasClaimsFilter ? nextElem.querySelectorAll('.claims-filter-match') : [];

            if (searchMarksInNext.length > 0 || claimsMarksInNext.length > 0) {
              foundInSection = true;
            }
            nextElem = nextElem.nextElementSibling;
          }
          if (!foundInSection) {
            (elem as HTMLElement).style.display = 'none';
          }
        } else if (!shouldShow) {
          (elem as HTMLElement).style.display = 'none';
        }
      });

      return doc.body.innerHTML;
    };

    // First reorder sections (only for HTML content), then apply highlighting
    const reordered = hasHTMLTags ? reorderSections(htmlContent) : htmlContent;
    const highlighted = highlightSearchQuery(reordered);
    const filtered = filterDescriptionByMatches(highlighted.html);

    // Remove [0000] paragraph numbers
    const cleanedFiltered = filtered.replace(/\[0{4}\]\s*/g, '').replace(/\[0\]\s*/g, '');

    // Debug logging - check browser console
    console.log('[DocumentViewer] Highlighting debug:', {
      pinnedCount: pinnedHighlights.length,
      pinnedTerms: pinnedHighlights.map(p => p.term),
      searchQuery: debouncedSearchQuery,
      hasPinnedMarks: cleanedFiltered.includes('pinned-highlight'),
      hasSearchMarks: cleanedFiltered.includes('search-match'),
    });

    // Update search match count
    if (highlighted.searchCount !== searchMatchCount) {
      setTimeout(() => {
        setSearchMatchCount(highlighted.searchCount);
        if (highlighted.searchCount > 0) {
          setTimeout(() => {
            const matches = descriptionRef.current?.querySelectorAll('.search-match');
            if (matches && matches[0]) {
              matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
              matches.forEach((m, i) => {
                if (i === 0) {
                  m.classList.add('ring-2', 'ring-blue-500');
                } else {
                  m.classList.remove('ring-2', 'ring-blue-500');
                }
              });
            }
          }, 100);
        }
      }, 0);
    }

    return cleanedFiltered;
  }, [documentContent?.description, debouncedSearchQuery, debouncedClaimsFilterText, claimsFilterEnabled, filterSearchResults, searchMatchCount, pinnedHighlights]);

  // Fix adjacent bold/italic markers that confuse markdown parsers
  const fixMarkdownFormatting = (content: string): string => {
    if (!content) return content;

    // Replace **text***italic* pattern with proper HTML, preserving what comes after
    // Match: **bold***italic*[anything after]
    // This handles cases like **28***a*,**28***b*and
    let fixed = content.replace(/\*\*([^*]+?)\*\*\*([^*]+?)\*([,;:.\s])?/g, (match, bold, italic, after) => {
      // If there's punctuation or space after, preserve it; otherwise add a space before letters
      const suffix = after || '';
      return `<b>${bold}</b><i>${italic}</i>${suffix}`;
    });

    // Handle cases where italic is followed immediately by a letter (like *b*and)
    fixed = fixed.replace(/<\/i>([a-zA-Z])/g, '</i> $1');

    // Also handle *italic***bold* pattern
    fixed = fixed.replace(/\*([^*]+?)\*\*\*([^*]+?)\*\*/g, '<i>$1</i><b>$2</b>');

    return fixed;
  };

  // Convert markdown to HTML (for use before highlighting)
  const markdownToHtml = (content: string): string => {
    if (!content) return content;

    // First fix problematic adjacent markers
    let html = fixMarkdownFormatting(content);

    // Convert remaining bold **text**
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<b>$1</b>');

    // Convert remaining italic *text*
    html = html.replace(/\*([^*]+?)\*/g, '<i>$1</i>');

    return html;
  };

  const handleCopyParagraph = async (paragraphNumber: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedParagraph(paragraphNumber);
      setTimeout(() => setCopiedParagraph(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Remove country and kind codes from patent/publication numbers
  const removeCountryAndKindCodes = (str: string): string => {
    if (!str) return str;

    // Remove country code if it starts with two uppercase letters
    if (/^[A-Z]{2}/.test(str)) {
      str = str.substring(2);
    }
    // Remove kind code if it ends with an alphabetic character followed by an optional alphanumeric character
    const kindCodeMatch = /[A-Z][A-Z0-9]?$/.exec(str);
    if (kindCodeMatch) {
      str = str.substring(0, str.length - kindCodeMatch[0].length);
    }

    return str;
  };

  // Handle PDF download for patents and publications
  const handleDownloadPdf = () => {
    if (!document) return;

    const docType = document.file_type || document.type || '';
    if (!['patent', 'publication'].includes(docType.toLowerCase())) return;

    // Get document ID
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      docId = numberMatch[0];
    }

    // For publications, remove country and kind codes
    const finalDocId = docType.toLowerCase() === 'publication'
      ? removeCountryAndKindCodes(docId)
      : docId;

    // Open USPTO PDF download URL
    const pdfUrl = `https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${finalDocId}`;
    window.open(pdfUrl, '_blank');
  };

  const getDocumentUrl = () => {
    if (!document) return null;

    // Get document ID and clean it
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');

    // For patents/publications, ensure US prefix
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      const number = numberMatch[0];
      // If it's a publication (starts with 20), don't add US prefix
      if (number.startsWith('20') && number.length >= 11) {
        return `/US${number}`;
      }
      // For patents (7-8 digits), add US prefix
      return `/US${number}`;
    }

    return null;
  };

  const handleOpenInNewTab = () => {
    const url = getDocumentUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (!document) {
    return null;
  }

  return (
    <div className={`${isFullWidth ? 'flex-1' : 'w-full'} border-l border-gray-200 bg-white flex flex-col h-full overflow-hidden`}>
      {/* Header */}
      <div className={`flex-shrink-0 border-b border-gray-200 transition-all ${compactMode ? 'px-4 py-2' : 'px-6 py-4'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className={`flex items-center gap-2 flex-wrap ${compactMode ? 'mb-1' : 'mb-2'}`}>
              <Badge variant="outline" className={`bg-blue-50 text-blue-700 border-blue-200 capitalize ${compactMode ? 'text-xs' : ''}`}>
                {documentType === 'foreign_patent' ? 'Foreign Patent' : documentType}
              </Badge>
              {document.resource_id && (
                <Badge variant="outline" className={compactMode ? 'text-xs' : ''}>
                  {document.resource_id.replace(/^(patent-|publication-|application-|foreign_patent-)/gi, '')}
                </Badge>
              )}
              {documentContent?.application_number && onViewApplication && (
                <div className="flex items-center gap-2">
                  <span className={`text-gray-600 ${compactMode ? 'text-xs' : 'text-sm'}`}>
                    Application: {documentContent.application_number}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onViewApplication(documentContent.application_number!)}
                    className={compactMode ? 'h-6 text-xs px-2' : 'h-7 text-xs'}
                  >
                    <FileText className={`mr-1 ${compactMode ? 'w-3 h-3' : 'w-3 h-3'}`} />
                    View Application
                  </Button>
                </div>
              )}
            </div>
            <h2 className={`font-semibold text-gray-900 ${compactMode ? 'text-base mb-0' : 'text-lg mb-1'}`}>
              {documentContent?.title || documentName}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCompactMode(!compactMode)}
              title={compactMode ? 'Expand header' : 'Compact header'}
              className={compactMode ? 'h-7 w-7 p-0' : 'h-9 w-9 p-0'}
            >
              {compactMode ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setShowTaskHistory(!showTaskHistory);
                if (!showTaskHistory && taskHistory.length === 0) {
                  // Fetch task history when opening for the first time
                  setTaskHistoryLoading(true);
                  try {
                    const history = await taskApi.getTaskHistory();
                    console.log('Fetched task history:', history);

                    // Get document identifier for filtering
                    let docId = document?.resource_id || document?.title || '';
                    docId = docId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');

                    // Remove country code and kind code - keep only numbers
                    const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                    console.log('Filtering for document:', cleanDocId);

                    // Filter history by document ID
                    const filtered = ((history as any).history || []).filter((item: any) => {
                      // Check various ID fields that might match
                      const itemDocId = item.doc_id || '';
                      const itemSelectedNum = item.selected_number || '';
                      const itemAppNum = item.application_number || '';

                      // Clean each field and check for match
                      const cleanItemDocId = itemDocId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                      const cleanItemSelectedNum = itemSelectedNum.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                      const cleanItemAppNum = itemAppNum.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');

                      return cleanItemDocId === cleanDocId ||
                             cleanItemSelectedNum === cleanDocId ||
                             cleanItemAppNum === cleanDocId;
                    });

                    console.log('Filtered history:', filtered);
                    setTaskHistory(filtered);
                  } catch (error) {
                    console.error('Failed to fetch task history:', error);
                  } finally {
                    setTaskHistoryLoading(false);
                  }
                }
              }}
              title="Task Results"
              className={compactMode ? 'h-7 text-xs px-2' : 'h-9'}
            >
              <History className={`${isFullWidth ? 'mr-2' : ''} ${compactMode ? 'w-3 h-3' : 'w-4 h-4'}`} />
              {isFullWidth && 'Task Results'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={compactMode ? 'h-7 px-2' : 'h-9 px-2'}
                  title="Change document font size"
                >
                  <Type className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {DOC_FONT_SIZE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setDocumentFontSize(option.value)}
                    className={`cursor-pointer ${documentFontSize === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <span style={{ fontSize: option.size }}>
                      {option.label}
                    </span>
                    {documentFontSize === option.value && (
                      <span className="ml-auto text-blue-600">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className={compactMode ? 'h-7 w-7' : ''}
            >
              <X className={compactMode ? 'w-4 h-4' : 'w-5 h-5'} />
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`flex items-center gap-2 flex-wrap ${compactMode ? 'mt-2' : 'mt-4'}`}>
          {/* Search input for patents/publications */}
          {!['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) && (
            <div className={`flex items-center ${compactMode ? 'gap-2' : 'gap-4'}`}>
              {/* Search Query */}
              <div className={`flex items-center gap-2 border border-gray-200 rounded-md bg-white ${compactMode ? 'px-2 py-1' : 'px-3 py-1.5'}`}>
                <Input
                  type="text"
                  placeholder={compactMode ? 'Search...' : 'Search in document...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`border-0 p-0 h-auto focus-visible:ring-0 shadow-none ${compactMode ? 'max-w-[150px] text-xs' : 'max-w-xs'}`}
                />
                {searchMatchCount > 0 && searchQuery.trim() && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-100 border border-yellow-300">
                      <span className="text-xs font-medium whitespace-nowrap text-yellow-900">
                        {currentSearchIndex + 1} / {searchMatchCount}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-yellow-200 text-yellow-900"
                        onClick={() => {
                          const newIndex = currentSearchIndex - 1 < 0
                            ? searchMatchCount - 1
                            : currentSearchIndex - 1;
                          setCurrentSearchIndex(newIndex);
                          setTimeout(() => {
                            const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                            const searchContext = activeTabContent || window.document;
                            const matches = searchContext.querySelectorAll('.search-match');
                            if (matches && matches[newIndex]) {
                              matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        }}
                        title="Previous match"
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-yellow-200 text-yellow-900"
                        onClick={() => {
                          const newIndex = currentSearchIndex + 1 >= searchMatchCount
                            ? 0
                            : currentSearchIndex + 1;
                          setCurrentSearchIndex(newIndex);
                          setTimeout(() => {
                            const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                            const searchContext = activeTabContent || window.document;
                            const matches = searchContext.querySelectorAll('.search-match');
                            if (matches && matches[newIndex]) {
                              matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        }}
                        title="Next match"
                      >
                        ↓
                      </Button>
                    </div>
                  </>
                )}
                {searchQuery && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={filterSearchResults}
                        onChange={(e) => setFilterSearchResults(e.target.checked)}
                        className="cursor-pointer"
                      />
                      Filter
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-yellow-100"
                      onClick={() => pinCurrentHighlight('search')}
                      disabled={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedSearchQuery.toLowerCase() && p.type === 'search')}
                      title={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedSearchQuery.toLowerCase() && p.type === 'search') ? 'Already pinned' : 'Pin this highlight'}
                    >
                      <Pin className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>

              {/* Reference Numeral Search - Only show for US patents/publications */}
              {documentType.toLowerCase() !== 'foreign_patent' && (
                <div className={`flex items-center gap-2 border border-gray-200 rounded-md bg-white ${compactMode ? 'px-2 py-1' : 'px-3 py-1.5'}`}>
                  <Input
                    type="text"
                    placeholder={compactMode ? 'Ref #' : 'Ref numeral (e.g., 100)'}
                    value={referenceNumeral}
                    onChange={(e) => setReferenceNumeral(e.target.value)}
                    className={`border-0 p-0 h-auto focus-visible:ring-0 shadow-none ${compactMode ? 'w-20 text-xs' : 'w-48'}`}
                  />
                  {referenceNumeralMatchCount > 0 && referenceNumeral.trim() && (
                    <>
                      <div className="h-4 w-px bg-gray-300" />
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 border border-blue-300">
                        <span className="text-xs font-medium whitespace-nowrap text-blue-900">
                          {currentReferenceNumeralIndex + 1} / {referenceNumeralMatchCount}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-blue-200 text-blue-900"
                          onClick={() => {
                            const newIndex = currentReferenceNumeralIndex - 1 < 0
                              ? referenceNumeralMatchCount - 1
                              : currentReferenceNumeralIndex - 1;
                            setCurrentReferenceNumeralIndex(newIndex);
                            setTimeout(() => {
                              const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                              const searchContext = activeTabContent || window.document;
                              const matches = searchContext.querySelectorAll('.reference-numeral-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Previous match"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-blue-200 text-blue-900"
                          onClick={() => {
                            const newIndex = currentReferenceNumeralIndex + 1 >= referenceNumeralMatchCount
                              ? 0
                              : currentReferenceNumeralIndex + 1;
                            setCurrentReferenceNumeralIndex(newIndex);
                            setTimeout(() => {
                              const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                              const searchContext = activeTabContent || window.document;
                              const matches = searchContext.querySelectorAll('.reference-numeral-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Next match"
                        >
                          ↓
                        </Button>
                      </div>
                    </>
                  )}
                  {referenceNumeral && (
                    <>
                      <div className="h-4 w-px bg-gray-300" />
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={filterReferenceNumerals}
                          onChange={(e) => setFilterReferenceNumerals(e.target.checked)}
                          className="cursor-pointer"
                        />
                        Filter
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-blue-100"
                        onClick={() => pinCurrentHighlight('reference')}
                        disabled={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedReferenceNumeral.toLowerCase() && p.type === 'reference')}
                        title={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedReferenceNumeral.toLowerCase() && p.type === 'reference') ? 'Already pinned' : 'Pin this highlight'}
                      >
                        <Pin className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pinned Highlights Display */}
          {pinnedHighlights.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-500">Pinned:</span>
              {pinnedHighlights.map((pinned, idx) => (
                <div
                  key={`${pinned.type}-${pinned.term}-${idx}`}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded ${pinned.color} text-black text-xs`}
                >
                  <span className="font-medium">{pinned.term}</span>
                  <button
                    onClick={() => unpinHighlight(pinned.term, pinned.type)}
                    className="hover:bg-black/10 rounded p-0.5"
                    title="Remove pinned highlight"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {pinnedHighlights.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs text-gray-500 hover:text-gray-700"
                  onClick={clearAllPinnedHighlights}
                  title="Clear all pinned highlights"
                >
                  Clear all
                </Button>
              )}
            </div>
          )}

          {/* Pin buttons for patents/publications */}
          {!['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) && documentContent?.claims && (
            <Button
              variant={claimsPinned ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={() => setClaimsPinned(!claimsPinned)}
              title={claimsPinned ? 'Unpin Claims' : 'Pin Claims'}
            >
              <Pin className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>{claimsPinned ? 'Unpin' : 'Pin'} Claims</>}
            </Button>
          )}

          {['patent', 'publication'].includes(documentType.toLowerCase()) && (
            <Button
              variant={drawingsPinned ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={() => {
                console.log('Pin Drawings clicked! Current state:', drawingsPinned, 'Document:', document);
                setDrawingsPinned(!drawingsPinned);
              }}
              title={drawingsPinned ? 'Unpin Drawings' : 'Pin Drawings'}
            >
              <ImageIcon className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>{drawingsPinned ? 'Unpin' : 'Pin'} Drawings</>}
            </Button>
          )}

          {['patent', 'publication', 'application', 'upload'].includes(documentType.toLowerCase()) && (
            <Button
              variant={showQuestionHistory ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleOpenQuestionHistory}
              title="Question History"
            >
              <MessageSquare className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Question History</>}
            </Button>
          )}

          {/* Comments Button */}
          <Button
            variant={showCommentsPanel ? 'default' : 'outline'}
            size="sm"
            className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
            onClick={() => setShowCommentsPanel(!showCommentsPanel)}
            title="Comments"
          >
            <MessageSquarePlus className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
            {isFullWidth && <>Comments</>}
            {comments.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {comments.length}
              </Badge>
            )}
          </Button>

          {getDocumentUrl() && (
            <Button
              variant="outline"
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleOpenInNewTab}
              title="Open in New Tab"
            >
              <ExternalLink className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Open in New Tab</>}
            </Button>
          )}

          {['patent', 'publication'].includes(documentType.toLowerCase()) && (
            <Button
              variant="outline"
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleDownloadPdf}
              title="Download PDF"
            >
              <Download className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Download PDF</>}
            </Button>
          )}
        </div>
      </div>

      {/* Document Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Loading document...</p>
          </div>
        </div>
      ) : isOnboarding ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
            <p className="text-gray-700 font-medium">Onboarding Document</p>
            <p className="text-gray-500 text-sm mt-1">This document is being loaded into your session...</p>
            <p className="text-gray-400 text-xs mt-2">This may take a minute</p>
          </div>
        </div>
      ) : !documentContent ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Failed to load document</p>
            <Button variant="outline" size="sm" onClick={loadDocument} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      ) : ['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) ? (
        // For MPEP, CFR, USC - show content directly without tabs
        <ScrollArea className="flex-1">
          <div className="px-6 py-6">
            {documentContent.content && (
              <div>
                {(documentContent as any).contentType === 'html' ? (
                  <div
                    className="prose max-w-none text-gray-700"
                    style={getFontSizeStyle()}
                    dangerouslySetInnerHTML={{ __html: documentContent.content }}
                  />
                ) : (
                  <div className="prose max-w-none">
                    <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                      {documentContent.content}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      ) : (
        // Patent/Publication layout with optional sidebars
        <div ref={contentRef} className="flex-1 flex flex-row overflow-hidden relative">
          <Tabs value={activeTab} onValueChange={setActiveTab} className={`flex flex-col overflow-hidden ${claimsPinned || drawingsPinned ? 'flex-1' : 'w-full'}`}>
            <div className="flex-shrink-0 border-b border-gray-200 px-6">
              <TabsList className="bg-transparent h-auto p-0 gap-4">
                <TabsTrigger value="overview" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                  Overview
                </TabsTrigger>
                {claims.length > 0 && !claimsPinned && (
                  <TabsTrigger value="claims" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Claims ({claims.length})
                  </TabsTrigger>
                )}
                {documentType?.toLowerCase() === 'upload' ? (
                  <TabsTrigger value="content" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Content
                  </TabsTrigger>
                ) : (
                  <TabsTrigger value="specification" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Specification
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

          <TabsContent value="overview" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6 space-y-6" style={getFontSizeStyle()}>
                {/* AI Summary */}
                {summary && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">AI Summary</h3>
                    {typeof summary === 'object' && summary !== null ? (
                      <div className="space-y-3">
                        {/* Show output content directly for uploads without section heading */}
                        {documentType?.toLowerCase() === 'upload' && (summary as any).output && (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            {(() => {
                              const outputStr = fixMarkdownFormatting(String((summary as any).output));
                              if (hasAnyHighlighting) {
                                const htmlContent = markdownToHtml(outputStr);
                                const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                                return (
                                  <div
                                    className="prose max-w-none text-gray-700 leading-relaxed"
                                    style={getFontSizeStyle()}
                                    dangerouslySetInnerHTML={{ __html: highlighted }}
                                  />
                                );
                              }
                              return (
                                <div className="prose max-w-none text-gray-700 leading-relaxed" style={getFontSizeStyle()}>
                                  <Markdown>{outputStr}</Markdown>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {Object.entries(summary)
                          .filter(([key]) => {
                            // Filter out token-related keys and "output" for uploads
                            const isUpload = documentType?.toLowerCase() === 'upload';
                            if (!isUpload) return true;
                            const excludeKeys = ['input_tokens', 'output_tokens', 'total_tokens', 'output'];
                            return !excludeKeys.includes(key.toLowerCase());
                          })
                          .map(([key, value]) => {
                          // Determine icon and colors based on key
                          const isProblem = key.toLowerCase().includes('problem');
                          const isSolution = key.toLowerCase().includes('solution');

                          const icon = isProblem ? AlertCircle : isSolution ? Lightbulb : null;
                          const iconColor = isProblem ? 'text-amber-600' : isSolution ? 'text-green-600' : 'text-blue-600';
                          const bgColor = isProblem ? 'bg-amber-50' : isSolution ? 'bg-green-50' : 'bg-blue-50';
                          const borderColor = isProblem ? 'border-amber-200' : isSolution ? 'border-green-200' : 'border-blue-200';

                          const Icon = icon;

                          const isUpload = documentType?.toLowerCase() === 'upload';

                          return (
                            <div key={key} className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
                              <div className="flex items-center gap-2 mb-3">
                                {Icon && <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />}
                                <h4 className="font-semibold text-gray-900">
                                  {key.replace(/_/g, ' ').split(' ').map(word =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}
                                </h4>
                              </div>
                              <div className="text-gray-700 leading-relaxed pl-7" style={getFontSizeStyle()}>
                                {(() => {
                                  const valueStr = String(value);

                                  if (hasAnyHighlighting) {
                                    const htmlContent = isUpload ? markdownToHtml(fixMarkdownFormatting(valueStr)) : valueStr;
                                    const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                                    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
                                  }

                                  if (isUpload) {
                                    return (
                                      <div className="prose max-w-none">
                                        <Markdown>{fixMarkdownFormatting(valueStr)}</Markdown>
                                      </div>
                                    );
                                  }

                                  return valueStr;
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        {(() => {
                          const summaryStr = fixMarkdownFormatting(String(summary));

                          if (hasAnyHighlighting) {
                            const htmlContent = markdownToHtml(summaryStr);
                            const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                            return (
                              <div
                                className="prose max-w-none text-gray-700"
                                style={getFontSizeStyle()}
                                dangerouslySetInnerHTML={{ __html: highlighted }}
                              />
                            );
                          }

                          return (
                            <div className="prose max-w-none text-gray-700 [&>p]:inline" style={getFontSizeStyle()}>
                              <Markdown>{summaryStr}</Markdown>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Abstract */}
                {documentContent.abstract && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Abstract</h3>
                    {(() => {
                      const abstractText = fixMarkdownFormatting(documentContent.abstract);

                      if (hasAnyHighlighting) {
                        // Convert markdown to HTML first, then apply highlighting
                        const htmlContent = markdownToHtml(abstractText);
                        const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                        return (
                          <div
                            className="prose max-w-none text-gray-600 leading-relaxed"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: highlighted }}
                          />
                        );
                      }

                      return (
                        <div className="prose max-w-none text-gray-600 leading-relaxed [&>p]:inline" style={getFontSizeStyle()}>
                          <Markdown>{abstractText}</Markdown>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Document Content (MPEP, CFR, USC) - Uploads moved to separate Content tab */}
                {documentContent.content && documentType?.toLowerCase() !== 'upload' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Content</h3>
                    {(() => {
                      const isHtml = (documentContent as any).contentType === 'html';
                      const content = documentContent.content;

                      if (hasAnyHighlighting) {
                        const highlighted = applyUnifiedHighlighting(content, { includeRefNum: true });
                        return (
                          <div
                            className="prose max-w-none text-gray-700"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: highlighted }}
                          />
                        );
                      }

                      if (isHtml) {
                        return (
                          <div
                            className="prose max-w-none text-gray-700"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: content }}
                          />
                        );
                      }

                      return (
                        <div className="prose max-w-none" style={getFontSizeStyle()}>
                          <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                            {content}
                          </pre>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Key Stats */}
                {(claims.length > 0 || sections.length > 0) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Document Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {claims.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-semibold text-gray-900">{claims.length}</div>
                          <div className="text-sm text-gray-600">Claims</div>
                        </div>
                      )}
                      {sections.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-semibold text-gray-900">
                            {sections.reduce((total, section) => total + section.paragraphs.length, 0)}
                          </div>
                          <div className="text-sm text-gray-600">Paragraphs</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {claims.length > 0 && (
            <TabsContent value="claims" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="px-6 py-6 space-y-4" style={getFontSizeStyle()}>
                  {claims.map((claim, index) => {
                    const claimText = claim.text || '';

                    // Apply highlighting to claim text
                    const query = debouncedSearchQuery.trim();
                    const claimsFilter = debouncedClaimsFilterText.trim();
                    const refNum = debouncedReferenceNumeral.trim();
                    let highlightedClaimText = claimText;
                    const hasHighlighting = query || claimsFilter || refNum || pinnedHighlights.length > 0;

                    if (query || claimsFilter || refNum) {
                      // First convert markdown to HTML
                      highlightedClaimText = markdownToHtml(highlightedClaimText);

                      // Apply multiple highlights simultaneously to avoid conflicts
                      if ((query && claimsFilter) || (query && refNum) || (claimsFilter && refNum) || (query && claimsFilter && refNum)) {
                        // Multiple filters active - need to handle overlaps
                        const matches: Array<{ start: number; end: number; type: 'search' | 'claims' | 'refnum'; text: string }> = [];
                        const lowerContent = highlightedClaimText.toLowerCase();

                        // Find all query matches
                        if (query) {
                          const lowerQuery = query.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + query.length,
                              type: 'search',
                              text: highlightedClaimText.substring(pos, pos + query.length)
                            });
                            pos++;
                          }
                        }

                        // Find all claims filter matches
                        if (claimsFilter) {
                          const lowerClaimsFilter = claimsFilter.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + claimsFilter.length,
                              type: 'claims',
                              text: highlightedClaimText.substring(pos, pos + claimsFilter.length)
                            });
                            pos++;
                          }
                        }

                        // Find all reference numeral matches (only in bold)
                        if (refNum) {
                          const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                          let match;
                          while ((match = refNumRegex.exec(highlightedClaimText)) !== null) {
                            matches.push({
                              start: match.index,
                              end: match.index + match[0].length,
                              type: 'refnum',
                              text: match[0]
                            });
                          }
                        }

                        // Sort and remove overlaps
                        matches.sort((a, b) => {
                          if (a.start !== b.start) return a.start - b.start;
                          return (b.end - b.start) - (a.end - a.start);
                        });

                        const usedRanges: typeof matches = [];
                        matches.forEach(match => {
                          const overlaps = usedRanges.some(range =>
                            (match.start >= range.start && match.start < range.end) ||
                            (match.end > range.start && match.end <= range.end) ||
                            (match.start <= range.start && match.end >= range.end)
                          );

                          if (!overlaps) {
                            usedRanges.push(match);
                          }
                        });

                        usedRanges.sort((a, b) => a.start - b.start);

                        // Build highlighted text
                        let result = '';
                        let lastEnd = 0;

                        usedRanges.forEach(match => {
                          if (match.start > lastEnd) {
                            result += highlightedClaimText.substring(lastEnd, match.start);
                          }

                          if (match.type === 'search') {
                            result += `<mark class="bg-yellow-300 text-black search-match">${match.text}</mark>`;
                          } else if (match.type === 'claims') {
                            result += `<mark class="bg-green-300 text-black claims-filter-match">${match.text}</mark>`;
                          } else if (match.type === 'refnum') {
                            result += `<mark class="bg-blue-300 text-black reference-numeral-match">${match.text}</mark>`;
                          }

                          lastEnd = match.end;
                        });

                        if (lastEnd < highlightedClaimText.length) {
                          result += highlightedClaimText.substring(lastEnd);
                        }

                        highlightedClaimText = result;
                      } else if (query) {
                        // Only search query
                        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>');
                      } else if (claimsFilter) {
                        // Only claims filter - always highlight if text is selected
                        const regex = new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>');
                      } else if (refNum) {
                        // Only reference numeral (must be in bold)
                        const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(refNumRegex, '<mark class="bg-blue-300 text-black reference-numeral-match"><b>$1</b></mark>');
                      }
                    }

                    // Apply pinned highlights to claims
                    if (pinnedHighlights.length > 0) {
                      // Convert markdown if not already converted
                      if (!query && !claimsFilter && !refNum) {
                        highlightedClaimText = markdownToHtml(highlightedClaimText);
                      }
                      pinnedHighlights.forEach(pinned => {
                        const escapedTerm = pinned.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        const regex = new RegExp(`(${escapedTerm})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, `<mark class="pinned-highlight" style="background-color: ${pinned.cssColor}; color: black;">$1</mark>`);
                      });
                    }

                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="font-medium text-gray-900 mb-2">
                          Claim {claim.claim_number || index + 1}
                        </div>
                        <div className="prose max-w-none text-gray-600 leading-relaxed" style={getFontSizeStyle()}>
                          {hasHighlighting ? (
                            <div dangerouslySetInnerHTML={{ __html: highlightedClaimText }} />
                          ) : (
                            <div className="[&>p]:inline">
                              <Markdown>{fixMarkdownFormatting(claimText)}</Markdown>
                            </div>
                          )}
                        </div>
                        {claim.dependencies && claim.dependencies.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Depends on: {claim.dependencies.join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
          )}

          {/* Content tab for uploads */}
          <TabsContent value="content" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6">
                {documentContent.content && (
                  <div className="prose max-w-none text-gray-700" style={getFontSizeStyle()}>
                    <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                      {documentContent.content}
                    </pre>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="specification" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6 space-y-6" ref={descriptionRef}>
                {processedDescription ? (
                  <div
                    className="prose max-w-none text-gray-700 patent-description"
                    dangerouslySetInnerHTML={{ __html: processedDescription }}
                    style={{
                      ...getFontSizeStyle(),
                      lineHeight: '1.75'
                    }}
                  />
                ) : sections.length > 0 ? (
                  sections.map((section, sectionIdx) => (
                    <div key={sectionIdx} className="space-y-4">
                      {/* Section Title */}
                      {section.title && (
                        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-300 pb-2">
                          {section.title}
                        </h3>
                      )}

                      {/* Section Paragraphs */}
                      <div className="space-y-3">
                        {section.paragraphs.map((para) => {
                          // Skip if no content
                          if (!para.content) return null;

                          // Apply highlighting to paragraph content
                          let highlightedContent = para.content;
                          const query = debouncedSearchQuery.trim();
                          const claimsFilter = debouncedClaimsFilterText.trim();
                          const refNum = debouncedReferenceNumeral.trim();
                          const hasPinnedHighlights = pinnedHighlights.length > 0;

                          if (query || claimsFilter || refNum || hasPinnedHighlights) {
                            // First convert markdown to HTML
                            highlightedContent = markdownToHtml(highlightedContent);

                            // Unified highlighting - collect ALL matches first, then apply in one pass
                            const matches: Array<{ start: number; end: number; type: string; text: string; cssColor?: string }> = [];
                            const lowerContent = highlightedContent.toLowerCase();

                            // Find all query matches
                            if (query) {
                              const lowerQuery = query.toLowerCase();
                              let pos = 0;
                              while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                                matches.push({
                                  start: pos,
                                  end: pos + query.length,
                                  type: 'search',
                                  text: highlightedContent.substring(pos, pos + query.length)
                                });
                                pos++;
                              }
                            }

                            // Find all claims filter matches
                            if (claimsFilter) {
                              const lowerClaimsFilter = claimsFilter.toLowerCase();
                              let pos = 0;
                              while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                                matches.push({
                                  start: pos,
                                  end: pos + claimsFilter.length,
                                  type: 'claims',
                                  text: highlightedContent.substring(pos, pos + claimsFilter.length)
                                });
                                pos++;
                              }
                            }

                            // Find all reference numeral matches (only in bold)
                            if (refNum) {
                              const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                              let match;
                              while ((match = refNumRegex.exec(highlightedContent)) !== null) {
                                matches.push({
                                  start: match.index,
                                  end: match.index + match[0].length,
                                  type: 'refnum',
                                  text: match[0]
                                });
                              }
                            }

                            // Find all pinned highlight matches
                            if (hasPinnedHighlights) {
                              pinnedHighlights.forEach(pinned => {
                                const lowerTerm = pinned.term.toLowerCase();
                                let pos = 0;
                                while ((pos = lowerContent.indexOf(lowerTerm, pos)) !== -1) {
                                  matches.push({
                                    start: pos,
                                    end: pos + pinned.term.length,
                                    type: 'pinned',
                                    text: highlightedContent.substring(pos, pos + pinned.term.length),
                                    cssColor: pinned.cssColor
                                  });
                                  pos++;
                                }
                              });
                            }

                            if (matches.length > 0) {
                              // Sort by length (longer first), then by start position
                              matches.sort((a, b) => {
                                const lenDiff = (b.end - b.start) - (a.end - a.start);
                                if (lenDiff !== 0) return lenDiff;
                                return a.start - b.start;
                              });

                              // Build non-overlapping ranges (longer matches take precedence)
                              const usedRanges: typeof matches = [];
                              matches.forEach(match => {
                                const overlaps = usedRanges.some(range =>
                                  (match.start >= range.start && match.start < range.end) ||
                                  (match.end > range.start && match.end <= range.end) ||
                                  (match.start <= range.start && match.end >= range.end)
                                );

                                if (!overlaps) {
                                  usedRanges.push(match);
                                }
                              });

                              // Sort by position for building final string
                              usedRanges.sort((a, b) => a.start - b.start);

                              // Build highlighted text
                              let result = '';
                              let lastEnd = 0;

                              usedRanges.forEach(match => {
                                if (match.start > lastEnd) {
                                  result += highlightedContent.substring(lastEnd, match.start);
                                }

                                if (match.type === 'search') {
                                  result += `<mark style="background-color: #fde047; color: black;" class="search-match">${match.text}</mark>`;
                                } else if (match.type === 'claims') {
                                  result += `<mark style="background-color: #86efac; color: black;" class="claims-filter-match">${match.text}</mark>`;
                                } else if (match.type === 'refnum') {
                                  result += `<mark style="background-color: #93c5fd; color: black;" class="reference-numeral-match">${match.text}</mark>`;
                                } else if (match.type === 'pinned') {
                                  result += `<mark style="background-color: ${match.cssColor}; color: black;" class="pinned-highlight">${match.text}</mark>`;
                                }

                                lastEnd = match.end;
                              });

                              if (lastEnd < highlightedContent.length) {
                                result += highlightedContent.substring(lastEnd);
                              }

                              highlightedContent = result;
                            }
                          }

                          // Check if paragraph should be shown when filtering
                          const hasSearchMatch = query && para.content.toLowerCase().includes(query.toLowerCase());
                          const hasClaimsMatch = claimsFilter && claimsFilterEnabled && para.content.toLowerCase().includes(claimsFilter.toLowerCase());
                          const hasRefNumMatch = refNum && new RegExp(`\\*\\*${refNum}[a-zA-Z]?\\*\\*`, 'i').test(para.content);

                          // Show paragraph if:
                          // - No filters are enabled, OR
                          // - Has a match for any enabled filter
                          const shouldShow = (!filterSearchResults && !claimsFilterEnabled && !filterReferenceNumerals)
                            || (filterSearchResults && hasSearchMatch)
                            || (claimsFilterEnabled && hasClaimsMatch)
                            || (filterReferenceNumerals && hasRefNumMatch);

                          if (!shouldShow) return null;

                          // Check if paragraph number is 0000 or 0 (hide if so)
                          const isZeroParagraph = para.number === '0000' || para.number === '0' || para.number === 0 || parseInt(para.number) === 0;

                          return (
                            <div key={para.number} className="group flex items-start gap-4">
                              {!isZeroParagraph && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-sm font-mono font-semibold text-gray-600 mt-1">
                                    [{para.number}]
                                  </span>
                                  <button
                                    onClick={() => handleCopyParagraph(para.number, para.content || '')}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                                    title="Copy paragraph"
                                  >
                                    {copiedParagraph === para.number ? (
                                      <Check className="w-3.5 h-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5 text-gray-500" />
                                    )}
                                  </button>
                                </div>
                              )}
                              <div className="text-gray-700 leading-relaxed flex-1" style={getFontSizeStyle()}>
                                {query || claimsFilter || refNum || hasPinnedHighlights ? (
                                  <div className="prose max-w-none" style={getFontSizeStyle()} dangerouslySetInnerHTML={{ __html: highlightedContent }} />
                                ) : (
                                  <div className="prose max-w-none [&>p]:inline" style={getFontSizeStyle()}>
                                    <Markdown>{fixMarkdownFormatting(para.content)}</Markdown>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No specification content available</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Pinned Drawings Sidebar */}
        {drawingsPinned && ['patent', 'publication'].includes(documentType.toLowerCase()) && (
          <div
            ref={drawingViewerRef}
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200"
            style={{
              width: selectedDrawingIndex !== null
                ? `${Math.max(drawingViewerWidth, window.innerWidth * 0.4)}px`
                : `${drawingViewerWidth}px`,
              maxWidth: '60vw'
            }}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-blue-500 ${isResizingDrawingWidth ? 'bg-blue-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingDrawingWidth(true);
              }}
              title="Drag to resize width"
            />

            <ScrollArea className="h-full">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDrawingIndex !== null ? (
                      selectedDrawingIndex === 0 ? (
                        <>Cover Sheet</>
                      ) : (
                        <>Sheet {selectedDrawingIndex}</>
                      )
                    ) : (
                      `Drawings${drawingsUrls.length > 0 ? ` (${drawingsUrls.length})` : ''}`
                    )}
                  </h3>
                  {selectedDrawingIndex !== null && (
                    <Button
                      onClick={() => {
                        setSelectedDrawingIndex(null);
                        setDrawingRotation(0);
                        setDrawingZoom(100);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      ← All
                    </Button>
                  )}
                </div>

                {/* Zoom and Rotation controls for full view */}
                {selectedDrawingIndex !== null && (
                  <div className="flex items-center gap-3 mb-4 justify-center">
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setDrawingZoom((prev) => Math.max(prev - 25, 25))}
                        variant="outline"
                        size="sm"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDrawingZoom(100)}
                        variant="outline"
                        size="sm"
                        className="min-w-[60px]"
                        title="Reset Zoom"
                      >
                        {drawingZoom}%
                      </Button>
                      <Button
                        onClick={() => setDrawingZoom((prev) => Math.min(prev + 25, 200))}
                        variant="outline"
                        size="sm"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                    </div>

                    <Separator orientation="vertical" className="h-6" />

                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setDrawingRotation((prev) => (prev - 90 + 360) % 360)}
                        variant="outline"
                        size="sm"
                        title="Rotate Left 90°"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDrawingRotation((prev) => (prev + 90) % 360)}
                        variant="outline"
                        size="sm"
                        title="Rotate Right 90°"
                      >
                        <RotateCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Drawing display */}
                {loadingDrawings ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <p className="text-gray-500 text-sm">Loading drawings...</p>
                  </div>
                ) : drawingsUrls.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <ImageIcon className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 text-sm">No drawings available</p>
                    <p className="text-gray-400 text-xs mt-1">Drawings may not be available for this document</p>
                  </div>
                ) : selectedDrawingIndex !== null ? (
                  <div className="flex justify-center items-center">
                    <img
                      src={drawingsUrls[selectedDrawingIndex]}
                      alt={selectedDrawingIndex === 0 ? 'Cover Sheet' : `Sheet ${selectedDrawingIndex}`}
                      className="max-w-full h-auto"
                      style={{
                        transform: `rotate(${drawingRotation}deg) scale(${drawingZoom / 100})`,
                        transformOrigin: 'center'
                      }}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {drawingsUrls.map((drawing: string, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedDrawingIndex(idx)}
                        className="border border-gray-200 rounded-lg p-2 hover:border-blue-500 hover:shadow-md transition-all"
                      >
                        <img
                          src={drawing}
                          alt={idx === 0 ? 'Cover Sheet' : `Sheet ${idx}`}
                          className="w-full h-auto"
                        />
                        <p className="text-xs text-gray-600 mt-2">
                          {idx === 0 ? 'Cover Sheet' : `Sheet ${idx}`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Pinned Claims Sidebar */}
        {claimsPinned && documentContent?.claims && (
          <div className="w-80 flex-shrink-0 min-w-0 border-l border-gray-200" ref={claimsSidebarRef}>
            <ScrollArea className="h-full">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Claims</h3>

                <div className="space-y-2 mb-4">
                  <p className="text-xs text-gray-600">
                    Select text from claims to highlight in specification
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100 flex-1">
                        <input
                          type="checkbox"
                          checked={claimsFilterEnabled}
                          onChange={(e) => setClaimsFilterEnabled(e.target.checked)}
                          className="cursor-pointer w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            Filter by selected text
                          </div>
                          {claimsFilterText && (
                            <div className="text-xs mt-1 text-gray-600">
                              "{claimsFilterText.length > 30 ? claimsFilterText.substring(0, 30) + '...' : claimsFilterText}"
                            </div>
                          )}
                        </div>
                      </label>
                      {claimsFilterText && (
                        <Button
                          onClick={() => {
                            setClaimsFilterText('');
                            setClaimsFilterEnabled(false);
                          }}
                          variant="destructive"
                          size="sm"
                        >
                          Clear
                        </Button>
                      )}
                    </div>

                    {/* Navigation controls for claims filter */}
                    {claimsFilterMatchCount > 0 && claimsFilterText.trim() && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 border border-green-300">
                        <span className="text-xs font-medium whitespace-nowrap text-green-900">
                          {currentClaimsFilterIndex + 1} / {claimsFilterMatchCount}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-green-200 text-green-900"
                          onClick={() => {
                            const newIndex = currentClaimsFilterIndex - 1 < 0
                              ? claimsFilterMatchCount - 1
                              : currentClaimsFilterIndex - 1;
                            setCurrentClaimsFilterIndex(newIndex);
                            setTimeout(() => {
                              const matches = window.document.querySelectorAll('.claims-filter-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Previous match"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-green-200 text-green-900"
                          onClick={() => {
                            const newIndex = currentClaimsFilterIndex + 1 >= claimsFilterMatchCount
                              ? 0
                              : currentClaimsFilterIndex + 1;
                            setCurrentClaimsFilterIndex(newIndex);
                            setTimeout(() => {
                              const matches = window.document.querySelectorAll('.claims-filter-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Next match"
                        >
                          ↓
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-4">
                  {claims.map((claim, idx) => {
                    const claimText = claim.text || '';
                    const isDependentClaim = /(?:according\s+to|of)\s+(?:any\s+of\s+)?claims?\s+\d+/i.test(claimText);

                    // Apply highlighting to claim text
                    const query = debouncedSearchQuery.trim();
                    const claimsFilter = debouncedClaimsFilterText.trim();
                    const refNum = debouncedReferenceNumeral.trim();
                    let highlightedClaimText = claimText;
                    const hasHighlighting = query || claimsFilter || refNum || pinnedHighlights.length > 0;

                    if (query || claimsFilter || refNum) {
                      // First convert markdown to HTML
                      highlightedClaimText = markdownToHtml(highlightedClaimText);

                      // Apply multiple highlights simultaneously to avoid conflicts
                      if ((query && claimsFilter) || (query && refNum) || (claimsFilter && refNum) || (query && claimsFilter && refNum)) {
                        // Multiple filters active - need to handle overlaps
                        const matches: Array<{ start: number; end: number; type: 'search' | 'claims' | 'refnum'; text: string }> = [];
                        const lowerContent = highlightedClaimText.toLowerCase();

                        // Find all query matches
                        if (query) {
                          const lowerQuery = query.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + query.length,
                              type: 'search',
                              text: highlightedClaimText.substring(pos, pos + query.length)
                            });
                            pos++;
                          }
                        }

                        // Find all claims filter matches
                        if (claimsFilter) {
                          const lowerClaimsFilter = claimsFilter.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + claimsFilter.length,
                              type: 'claims',
                              text: highlightedClaimText.substring(pos, pos + claimsFilter.length)
                            });
                            pos++;
                          }
                        }

                        // Find all reference numeral matches (only in bold)
                        if (refNum) {
                          const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                          let match;
                          while ((match = refNumRegex.exec(highlightedClaimText)) !== null) {
                            matches.push({
                              start: match.index,
                              end: match.index + match[0].length,
                              type: 'refnum',
                              text: match[0]
                            });
                          }
                        }

                        // Sort and remove overlaps
                        matches.sort((a, b) => {
                          if (a.start !== b.start) return a.start - b.start;
                          return (b.end - b.start) - (a.end - a.start);
                        });

                        const usedRanges: typeof matches = [];
                        matches.forEach(match => {
                          const overlaps = usedRanges.some(range =>
                            (match.start >= range.start && match.start < range.end) ||
                            (match.end > range.start && match.end <= range.end) ||
                            (match.start <= range.start && match.end >= range.end)
                          );

                          if (!overlaps) {
                            usedRanges.push(match);
                          }
                        });

                        usedRanges.sort((a, b) => a.start - b.start);

                        // Build highlighted text
                        let result = '';
                        let lastEnd = 0;

                        usedRanges.forEach(match => {
                          if (match.start > lastEnd) {
                            result += highlightedClaimText.substring(lastEnd, match.start);
                          }

                          if (match.type === 'search') {
                            result += `<mark class="bg-yellow-300 text-black search-match">${match.text}</mark>`;
                          } else if (match.type === 'claims') {
                            result += `<mark class="bg-green-300 text-black claims-filter-match">${match.text}</mark>`;
                          } else if (match.type === 'refnum') {
                            result += `<mark class="bg-blue-300 text-black reference-numeral-match">${match.text}</mark>`;
                          }

                          lastEnd = match.end;
                        });

                        if (lastEnd < highlightedClaimText.length) {
                          result += highlightedClaimText.substring(lastEnd);
                        }

                        highlightedClaimText = result;
                      } else if (query) {
                        // Only search query
                        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>');
                      } else if (claimsFilter) {
                        // Only claims filter - always highlight if text is selected
                        const regex = new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>');
                      } else if (refNum) {
                        // Only reference numeral (must be in bold)
                        const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(refNumRegex, '<mark class="bg-blue-300 text-black reference-numeral-match"><b>$1</b></mark>');
                      }
                    }

                    // Apply pinned highlights to sidebar claims
                    if (pinnedHighlights.length > 0) {
                      // Convert markdown if not already converted
                      if (!query && !claimsFilter && !refNum) {
                        highlightedClaimText = markdownToHtml(highlightedClaimText);
                      }
                      pinnedHighlights.forEach(pinned => {
                        const escapedTerm = pinned.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        const regex = new RegExp(`(${escapedTerm})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, `<mark class="pinned-highlight" style="background-color: ${pinned.cssColor}; color: black;">$1</mark>`);
                      });
                    }

                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border-l-4 ${
                          isDependentClaim
                            ? 'border-blue-400 bg-blue-50/50'
                            : 'border-green-500 bg-green-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                            isDependentClaim
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {claim.claim_number || idx + 1}
                          </div>
                          <div className="flex-1 select-text cursor-text">
                            <div className="prose max-w-none leading-relaxed text-gray-700" style={getFontSizeStyle()}>
                              {hasHighlighting ? (
                                <div dangerouslySetInnerHTML={{ __html: highlightedClaimText }} />
                              ) : (
                                <div className="[&>p]:inline">
                                  <Markdown>{fixMarkdownFormatting(claimText)}</Markdown>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Task History Panel */}
        {showTaskHistory && (
          <div
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200 flex flex-col bg-white"
            style={{
              width: `${taskHistoryWidth}px`,
              minWidth: '300px',
              maxWidth: '50vw'
            }}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-purple-500 ${isResizingTaskHistory ? 'bg-purple-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingTaskHistory(true);
              }}
              title="Drag to resize width"
            />

            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Task Results</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowTaskHistory(false)}
                  className="h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                {taskHistory.length} task{taskHistory.length !== 1 ? 's' : ''} found for this document
              </p>
            </div>

            {/* Task History Content */}
            <ScrollArea className="flex-1">
              <div ref={taskHistoryScrollRef} className="p-4 space-y-3">
                {taskHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Loading task history...</p>
                    </div>
                  </div>
                ) : taskHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">No tasks found for this document</p>
                  </div>
                ) : (
                  taskHistory.map((task, index) => {
                    const taskName = task.task_name || task.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    // Handle timestamp - use time_received or timestamp field
                    let timestamp = 'Unknown date';
                    try {
                      const timeValue = (task as any).time_received || (task as any).timestamp;
                      if (timeValue) {
                        const date = new Date(timeValue);
                        if (!isNaN(date.getTime())) {
                          timestamp = date.toLocaleString();
                        }
                      }
                    } catch (e) {
                      console.error('Invalid timestamp:', (task as any).time_received, (task as any).timestamp);
                    }

                    return (
                      <div
                        key={task.analysis_id || index}
                        className="border border-gray-200 rounded-lg p-3 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                        onClick={() => {
                          // Extract the actual result data - use 'analysis' or 'result' field
                          let resultContent = (task as any).analysis || (task as any).result;

                          // If result has nested structure, extract it
                          if (resultContent && typeof resultContent === 'object') {
                            if (resultContent.result !== undefined) {
                              resultContent = resultContent.result;
                            } else if (resultContent.output !== undefined) {
                              resultContent = resultContent.output;
                            } else if (resultContent.analysis !== undefined) {
                              resultContent = resultContent.analysis;
                            }
                          }

                          // Convert to string if it's an object
                          const contentStr = typeof resultContent === 'string'
                            ? resultContent
                            : (resultContent ? JSON.stringify(resultContent) : '{}');

                          // Get timestamp
                          const timeValue = (task as any).time_received || (task as any).timestamp;
                          const timestampStr = timeValue ? new Date(timeValue).getTime().toString() : Date.now().toString();

                          // Create a TaskResultAttachment from the history item
                          const taskResult: TaskResultAttachment = {
                            id: task.analysis_id,
                            taskName: taskName,
                            taskType: task.task_type,
                            timestamp: timestampStr,
                            content: contentStr,
                            documentId: task.doc_id || task.selected_number || task.application_number
                          };
                          setSelectedTaskResult(taskResult);
                          setShowTaskResultViewer(true);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {taskName}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {task.task_type}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {timestamp}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Question History Panel */}
        {showQuestionHistory && ['patent', 'publication', 'application', 'upload'].includes(documentType.toLowerCase()) && (
          <div
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200 flex flex-col"
            style={{
              width: `${questionWidth}px`,
              minWidth: '300px',
              maxWidth: '50vw'
            }}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-blue-500 ${isResizingQuestion ? 'bg-blue-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingQuestion(true);
              }}
              title="Drag to resize width"
            />

            <CustomQuestionSection
              showQuestionHistory={showQuestionHistory}
              setShowQuestionHistory={setShowQuestionHistory}
              questions={questions}
              questionText={questionText}
              setQuestionText={setQuestionText}
              questionLoading={questionLoading}
              handleQuestionSubmit={handleQuestionSubmit}
              scrollDivRef={questionScrollRef}
              showTopButton={showTopButton}
              showBottomButton={showBottomButton}
              scrollToTop={scrollToTop}
              scrollToBottom={scrollToBottom}
              toggleFullscreen={toggleQuestionHistoryFullscreen}
              isFullscreen={false}
              onDeleteQuestion={handleDeleteQuestion}
              customPrompts={filteredCustomPrompts}
              standardPrompts={standardDocumentPrompts}
              onCreatePrompt={handleCreatePrompt}
            />
          </div>
        )}

        {/* Fullscreen Question History Modal */}
        <Dialog open={showQuestionHistoryFullscreen} onOpenChange={setShowQuestionHistoryFullscreen}>
          <DialogContent className="!max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col sm:!max-w-[95vw] md:!max-w-[95vw] lg:!max-w-[95vw] [&>button]:hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-xl font-semibold text-gray-900">Question History - {documentName}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuestionHistoryFullscreen(false)}
                  className="h-8 w-8 p-0"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Messages Container */}
              <div ref={questionScrollRef} onScroll={handleScroll} className="relative flex-1 overflow-y-auto py-6 space-y-4 bg-white">
                {questions && questions.length > 0 ? (
                  <>
                    {questions.map((item, idx) => {
                      const isValidTimestamp = !isNaN(new Date(item.timestamp).getTime());

                      // Handle response in different formats
                      let responseText = '';
                      if (typeof item.response === 'string') {
                        responseText = item.response;
                      } else if (typeof item.response === 'object' && item.response.output) {
                        responseText = item.response.output;
                      } else {
                        responseText = JSON.stringify(item.response);
                      }

                      return (
                        <div key={idx} className="w-full px-4">
                          {/* Divider */}
                          {idx > 0 && (
                            <div className="flex justify-center my-6">
                              <div className="w-full border-t border-gray-300"></div>
                            </div>
                          )}

                          {/* Timestamp */}
                          {isValidTimestamp && (
                            <div className="text-center text-sm text-gray-500 mb-3">
                              {new Date(item.timestamp).toLocaleString()}
                            </div>
                          )}

                          {/* Question (Right-aligned, blue) */}
                          <div className="flex justify-end mb-3 group">
                            <div className="flex items-start gap-2">
                              <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(item.input_text);
                                    } catch (error) {
                                      console.error('Failed to copy question:', error);
                                    }
                                  }}
                                  title="Copy question"
                                >
                                  <Copy className="w-4 h-4 text-gray-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  onClick={() => handleDeleteQuestion('', idx)}
                                  title="Delete question"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="max-w-[75%] p-4 rounded-lg bg-blue-500 text-white shadow-md">
                                <div className="whitespace-pre-wrap break-words" style={getFontSizeStyle()}>
                                  {item.input_text.replace(/\n{2,}/g, '\n')}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Response (Left-aligned, gray) */}
                          <div className="flex justify-start mb-3 group">
                            <div className="flex items-start gap-2">
                              <div className="max-w-[75%] p-4 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 shadow-md">
                                {responseText === '...' ? (
                                  <div className="flex items-center gap-2 text-gray-500">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Generating response...</span>
                                  </div>
                                ) : (
                                  <div className="prose max-w-none text-gray-900" style={getFontSizeStyle()}>
                                    <Markdown>{responseText}</Markdown>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(responseText);
                                    } catch (error) {
                                      console.error('Failed to copy response:', error);
                                    }
                                  }}
                                  title="Copy response"
                                >
                                  <Copy className="w-4 h-4 text-gray-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  onClick={() => handleDeleteQuestion('', idx)}
                                  title="Delete conversation"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-lg">
                    No question history yet. Ask a question about this document!
                  </div>
                )}

                {/* Scroll buttons */}
                {showTopButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-6 right-6 rounded-full shadow-lg"
                    onClick={scrollToTop}
                    title="Scroll to top"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                )}
                {showBottomButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute bottom-6 right-6 rounded-full shadow-lg"
                    onClick={scrollToBottom}
                    title="Scroll to bottom"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="w-full px-8">
                  <div className="flex gap-4">
                    <textarea
                      className="flex-grow resize-none min-h-[80px] max-h-[200px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (questionText.trim() && !questionLoading) {
                            handleQuestionSubmit();
                          }
                        }
                      }}
                      placeholder="Ask a question about this document..."
                      disabled={questionLoading}
                    />
                    <div className="flex flex-col gap-2">
                      {/* Quick Prompts Dropdown */}
                      {((filteredCustomPrompts && filteredCustomPrompts.length > 0) || (standardDocumentPrompts && standardDocumentPrompts.length > 0)) && (
                        <DropdownMenu open={showQuickPromptsMenu} onOpenChange={setShowQuickPromptsMenu}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="px-3"
                              disabled={questionLoading}
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Quick Prompts
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
                            {/* Create New Prompt Button */}
                            <DropdownMenuItem
                              onClick={handleCreatePrompt}
                              className="cursor-pointer text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Create New Prompt
                            </DropdownMenuItem>
                            <div className="border-t border-gray-200 my-1" />

                            {/* Custom Prompts Section */}
                            {filteredCustomPrompts && filteredCustomPrompts.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Your Custom Prompts
                                </div>
                                {filteredCustomPrompts.map((prompt) => (
                                  <DropdownMenuItem
                                    key={prompt.prompt_id}
                                    onClick={() => {
                                      setQuestionText(prompt.prompt);
                                      setShowQuickPromptsMenu(false);
                                    }}
                                    className="cursor-pointer py-2"
                                  >
                                    <div className="flex flex-col gap-1">
                                      <span className="font-medium text-sm">{prompt.label}</span>
                                      <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                                <div className="border-t border-gray-200 my-1" />
                              </>
                            )}

                            {/* Standard Document Prompts Section */}
                            {standardDocumentPrompts && standardDocumentPrompts.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Standard Document Prompts
                                </div>
                                {standardDocumentPrompts.map((prompt) => (
                                  <DropdownMenuItem
                                    key={prompt.prompt_id}
                                    onClick={() => {
                                      setQuestionText(prompt.prompt);
                                      setShowQuickPromptsMenu(false);
                                    }}
                                    className="cursor-pointer py-2"
                                  >
                                    <div className="flex flex-col gap-1">
                                      <span className="font-medium text-sm">{prompt.label}</span>
                                      <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        onClick={handleQuestionSubmit}
                        disabled={questionLoading || !questionText.trim()}
                        className="px-8 h-auto"
                      >
                        {questionLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Submit'
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    Press Enter to submit, Shift+Enter for new line
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Task Result Viewer Overlay */}
        {showTaskResultViewer && selectedTaskResult && (
          <div className="fixed inset-y-0 right-0 w-[600px] bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <FileText className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 truncate">{selectedTaskResult.taskName}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {(() => {
                        try {
                          const ts = typeof selectedTaskResult.timestamp === 'number'
                            ? (selectedTaskResult.timestamp < 10000000000 ? selectedTaskResult.timestamp * 1000 : selectedTaskResult.timestamp)
                            : selectedTaskResult.timestamp;
                          const date = new Date(ts);
                          return isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleString();
                        } catch (e) {
                          return 'Unknown date';
                        }
                      })()}
                    </p>
                    {selectedTaskResult.documentId && (
                      <p className="text-xs text-purple-600 mt-1">
                        Document ID: {selectedTaskResult.documentId}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowTaskResultViewer(false);
                    setSelectedTaskResult(null);
                  }}
                  className="flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
              <div className="p-6">
                {renderTaskResultDisplay(selectedTaskResult)}
              </div>
            </ScrollArea>
          </div>
        )}
        </div>
      )}

      {/* Comment Popup - appears when text is selected */}
      {showCommentPopup && (
        <div
          className="fixed z-[100] bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-80"
          style={{
            left: Math.min(Math.max(10, commentPopupPosition.x - 160), window.innerWidth - 340),
            // If near bottom, position popup ABOVE the selection point
            top: commentPopupPosition.y > window.innerHeight - 250
              ? Math.max(10, commentPopupPosition.y - 220)  // Position above
              : Math.min(commentPopupPosition.y + 10, window.innerHeight - 250),  // Position below
          }}
        >
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-gray-500">Selected text:</div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => {
                  navigator.clipboard.writeText(selectedText);
                  addNotification({ type: 'success', title: 'Copied', message: 'Text copied to clipboard', duration: 2000 });
                }}
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
            <div className="text-sm bg-yellow-50 p-2 rounded border border-yellow-200 max-h-20 overflow-y-auto">
              "{selectedText.length > 100 ? selectedText.substring(0, 100) + '...' : selectedText}"
            </div>
          </div>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Add your comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCommentPopup(false);
                setSelectedText('');
                setNewCommentText('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={addComment}
              disabled={!newCommentText.trim()}
            >
              <MessageSquarePlus className="w-3 h-3 mr-1" />
              Add Comment
            </Button>
          </div>
        </div>
      )}

      {/* Comments Panel - slides in from the right */}
      {showCommentsPanel && (
        <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
          {/* Panel Header */}
          <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Comments</h3>
                <Badge variant="secondary">{comments.length}</Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowCommentsPanel(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select text in the document to add a comment
            </p>
          </div>

          {/* Comments List - with proper scroll container */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-4 space-y-3">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No comments yet</p>
                  <p className="text-xs mt-1">Select text to add a comment</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    {/* Selected text quote */}
                    <div className="text-xs bg-yellow-50 p-2 rounded border-l-2 border-yellow-400 mb-2 text-gray-700 italic">
                      "{comment.selected_text.length > 80 ? comment.selected_text.substring(0, 80) + '...' : comment.selected_text}"
                    </div>

                    {/* Comment text or edit form */}
                    {editingCommentId === comment.id ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingCommentText('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => updateComment(comment.id)}
                            disabled={!editingCommentText.trim()}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-800">{comment.comment_text}</div>
                    )}

                    {/* Meta and actions */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs text-gray-400">
                        {new Date(comment.created_at).toLocaleDateString()}
                        {comment.section && <span className="ml-2">• {comment.section}</span>}
                      </div>
                      {editingCommentId !== comment.id && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditingCommentText(comment.comment_text);
                            }}
                            title="Edit comment"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700"
                            onClick={() => deleteComment(comment.id)}
                            title="Delete comment"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Prompt Modal */}
      <CustomPromptModal
        open={showCustomPromptModal}
        onOpenChange={async (open) => {
          setShowCustomPromptModal(open);
          if (!open) {
            setEditingPrompt(null);
            // Refresh prompts when modal closes
            try {
              const response = await userApi.fetchPrompts();
              if (response.prompts) {
                setCustomPrompts(response.prompts);
              }
            } catch (error) {
              console.error('Failed to refresh prompts on modal close:', error);
            }
          }
        }}
        onSave={handleSaveCustomPrompt}
        editingPrompt={editingPrompt}
      />
    </div>
  );
}
